/**
 * Formalities Check — Status tab, step 1
 * Figma: node-id=167-3061 (FC Pass), 168-5897 (FC Fail / Status of FC Failure),
 *        168-6096 (FC Fail / Notice to Client)
 *
 * FC Pass → TM selector + Message Preview + WhatsApp send
 * FC Fail → TM selector + two-column panel:
 *           left: FC Fail sub-steps list
 *           right: detail form for selected sub-step
 */
import { useState, useEffect, useLayoutEffect, useMemo, useRef, useImperativeHandle, forwardRef } from "react";
import { useSearchParams } from "react-router-dom";
import { HiOutlineX } from "react-icons/hi";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import { toast } from "sonner";
import { getWorkflowStatus, skipStep } from "api/applications";
import {
  getLeadDetails,
  getAcknowledgementSummary,
  getFormalitiesCheckStatus,
  getFcPassInformClientPreview,
  sendFcPassInformClientWhatsApp,
  getFormalitiesCheckFailStatus,
  getFcFailInformClientPreview,
  sendFcFailInformClientWhatsApp,
  sendFcFailNoticeToClientWhatsApp,
  getFcFailNoticeToClientPreview,
  type FcFailNoticeToClientPreview,
  getFcReplyInvoiceStatus,
  getFcReplyInvoicePreview,
  saveFcReplyInvoice,
  generateFcReplyInvoicePdf,
  sendFcReplyInvoiceToClient,
  getFcReplyInvoicePaymentMessagePreview,
  type FcReplyInvoiceEmailPreview,
  recordCallClientFcFail,
  getFcFailConfirmationFromClient,
  saveFcFailConfirmationFromClient,
  recordDocumentsFromClientFcFail,
  getFcReplyFiledStatus,
  generateFcReplyFiledPresignedUrls,
  saveFcReplyFiled,
  getFcReplyFiledInformClientPreview,
  sendFcReplyFiledInformClientWhatsApp,
  setFormalitiesCheckFailData,
  generateAcknowledgementUploadUrls,
  type FormalitiesCheckStatusApplication,
  type FormalitiesCheckFailStatus,
  type FcReplyInvoiceStatus,
  type FcReplyInvoicePreview,
  type FcReplyInvoiceTmRow,
  type FcFailConfirmationStatus,
  type FcReplyFiledStatus,
  type FcReplyFiledApplication,
} from "api/discussion";
import DateTimePicker from "components/DateTimePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import legalAdviceHeadset from "assets/svg/legal-advice-headset.svg";
import pdfIcon from "assets/svg/pdf-icon.svg";
import collectDocuments from "assets/svg/collectDocuments.svg";
import radioCheck from "assets/svg/radio-check.svg";
import invoiceAmountCheck from "assets/svg/invoice-amount-check.svg";
import invoiceGenerate from "assets/svg/invoice-generate.svg";
import ChevronDownIcon from "assets/svg/chevron-down.svg";
import { formatTmNoForDisplay } from "utils/tmNumberDisplay";
import { renderWhatsAppFormatting } from "lib/whatsappFormatting";
import FilePreviewModal, { detectPreviewKind } from "components/FilePreviewModal";
import type { FilePreviewKind } from "components/FilePreviewModal";
import ConfirmMessageModal from "components/ConfirmMessageModal";
import { Checkbox } from "components/ui/checkbox";
import {
  EmailPreviewBlock,
  WhatsAppMessageBlock,
} from "components/InvoiceMessagePreview";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// ---------------------------------------------------------------------------
// Shared typography token for all toggle / pill buttons
// ---------------------------------------------------------------------------

/**
 * Figma spec for toggle button labels:
 *   font-family: Inter
 *   font-weight: 500 (Medium)
 *   font-size: 12px
 *   line-height: 20px
 *   letter-spacing: 0px
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type FCStatus = "pass" | "fail";

type FcNoticeFileRow = {
  tmNo: string;
  fileKey: string | null;
  filename: string | null;
  fileUrl: string | null;
  pendingFile: File | null;
};

type TmEntry = {
  tmNo: string;
  mark: string;
};

export type FormalitiesCheckViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
  onSavingChange?: (saving: boolean) => void;
  onSentStatusChange?: (sent: boolean) => void;
  /** When true, dashboard footer uses "Send" and submits FC Pass form */
  onFcPassFooterSendModeChange?: (needsSend: boolean) => void;
  /** Save-mode counterpart for FC Pass (after sent: footer "Save" + inline Resend, no Continue) */
  onFcPassFooterSaveModeChange?: (needsSave: boolean) => void;
  /** When true, dashboard footer uses "Send & Continue" and submits FC Fail Notice form */
  onFcFailNoticeFooterSendModeChange?: (needsSend: boolean) => void;
  /** When true, dashboard footer uses "Send & Continue" and submits FC Fail Inform Client form */
  onFcFailInformFooterSendModeChange?: (needsSend: boolean) => void;
  /** When true, dashboard footer uses "Send & Continue" and submits FC Fail Invoice WhatsApp send */
  onFcFailInvoiceFooterSendModeChange?: (needsSend: boolean) => void;
  /** When true, dashboard footer uses "Send & Continue" and submits FC Fail Inform Client (Done) WhatsApp */
  onFcFailInformDoneFooterSendModeChange?: (needsSend: boolean) => void;
  /** When true, dashboard footer's "Save & Continue" submits FC Fail Status form (advances to next FC Fail sub-step) */
  onFcFailStatusFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Save-mode counterpart for FC Fail Notice (after sent: footer Save & Continue advances) */
  onFcFailNoticeFooterSaveModeChange?: (needsSave: boolean) => void;
  /** Save-mode counterpart for FC Fail Inform Client (after sent: footer Save & Continue advances) */
  onFcFailInformFooterSaveModeChange?: (needsSave: boolean) => void;
  /** Save-mode counterpart for FC Fail Invoice (after sent: footer Save & Continue advances) */
  onFcFailInvoiceFooterSaveModeChange?: (needsSave: boolean) => void;
  /** Save-mode counterpart for FC Fail Inform Client (Done) (after sent: footer Save & Continue advances) */
  onFcFailInformDoneFooterSaveModeChange?: (needsSave: boolean) => void;
  /** FC Fail Call Client: footer Save & Continue submits the form (advances) */
  onFcFailCallClientFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** FC Fail Client Confirmation: footer Save & Continue submits the form (advances) */
  onFcFailConfirmationFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** FC Fail Documents: footer Save & Continue submits the form (advances) */
  onFcFailDocumentsFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** FC Fail Filing of Reply: footer Save & Continue submits the form (advances) */
  onFcFailFilingOfReplyFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Register a Progress handler with the dashboard footer for the FC Fail Invoice sub-step. */
  onFcFailInvoiceProgressActionChange?: (handler: (() => Promise<void>) | null) => void;
};

// ---------------------------------------------------------------------------
// FC Fail sub-steps
// ---------------------------------------------------------------------------

const FC_FAIL_SUB_STEPS = [
  { id: "status-of-fc-failure", label: "Status of FC Failure" },
  { id: "notice-to-client", label: "Notice to Client" },
  { id: "inform-client", label: "Inform Client" },
  { id: "invoice-for-fc-failure", label: "Invoice for FC Failure" },
  { id: "call-client", label: "Call Client" },
  { id: "client-confirmation", label: "Client Confirmation" },
  { id: "documents", label: "Documents" },
  { id: "filing-of-reply", label: "Filing of Reply" },
  { id: "inform-client-done", label: "Inform Client (Done)" },
] as const;

type FCFailSubStep = (typeof FC_FAIL_SUB_STEPS)[number]["id"];

// ---------------------------------------------------------------------------
// Shared icon primitives
// ---------------------------------------------------------------------------

/**
 * Review item 59 — multi-application affordance.
 *
 * The review accepts that a step must be completed for every application before
 * moving on ("that is ok"), but asks that the options to enter the next application
 * be *shown* so staff know they exist. Two things were missing: no indication of
 * position within the set, and — on the FC Pass tab — no hint that FC Failure
 * applications exist at all.
 */
function MultiApplicationNav({
  total,
  currentIdx,
  onSelectIdx,
  otherBranchCount,
  otherBranchLabel,
  onSwitchBranch,
}: {
  total: number;
  currentIdx: number;
  onSelectIdx: (idx: number) => void;
  otherBranchCount: number;
  otherBranchLabel: string;
  onSwitchBranch: () => void;
}) {
  const showPosition = total > 1;
  const showOtherBranch = otherBranchCount > 0;
  if (!showPosition && !showOtherBranch) return null;

  const atFirst = currentIdx <= 0;
  const atLast = currentIdx >= total - 1;

  return (
    <div
      className="flex flex-col gap-2 self-stretch rounded-[8px] border border-[#EEEEEE] bg-[#FCFCFC] px-3.5 py-3 sm:flex-row sm:items-center sm:justify-between"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {showPosition ? (
        <span className="text-[12px] leading-5 text-[#6D6D6D]">
          Application{" "}
          <span className="font-medium text-[#222222]">{currentIdx + 1}</span> of{" "}
          <span className="font-medium text-[#222222]">{total}</span> — this step
          must be completed for each application.
        </span>
      ) : (
        <span className="text-[12px] leading-5 text-[#6D6D6D]">
          {otherBranchCount}{" "}
          {otherBranchCount === 1 ? "application" : "applications"} on this
          acknowledgement {otherBranchCount === 1 ? "is" : "are"} in{" "}
          {otherBranchLabel}.
        </span>
      )}

      <div className="flex flex-row flex-wrap items-center gap-2">
        {showPosition && (
          <>
            <button
              type="button"
              onClick={() => onSelectIdx(currentIdx - 1)}
              disabled={atFirst}
              className="h-8 px-3 text-xs font-medium text-[#222222] bg-white border border-[#D5D3E0] rounded-[8px] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous application
            </button>
            <button
              type="button"
              onClick={() => onSelectIdx(currentIdx + 1)}
              disabled={atLast}
              className="h-8 px-3 text-xs font-medium text-[#222222] bg-white border border-[#D5D3E0] rounded-[8px] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next application
            </button>
          </>
        )}
        {showPosition && showOtherBranch && (
          <span className="hidden sm:inline text-[12px] leading-5 text-[#D5D3E0]">
            |
          </span>
        )}
        {showOtherBranch && (
          <button
            type="button"
            onClick={onSwitchBranch}
            className="h-8 px-3 text-xs font-medium text-[#222222] bg-white border border-[#222222] rounded-[8px] hover:bg-gray-50"
          >
            Go to {otherBranchLabel} ({otherBranchCount})
          </button>
        )}
      </div>
    </div>
  );
}

function RadioCircle({ active }: { active: boolean }) {
  if (active) {
    return (
      <img
        src={radioCheck}
        alt=""
        width={14}
        height={14}
        className="flex-shrink-0"
      />
    );
  }
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="flex-shrink-0"
    >
      <circle cx="7" cy="7" r="6.5" stroke="#6D6D6D" />
    </svg>
  );
}

function ChevronRight({ active }: { active: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="flex-shrink-0"
    >
      <path
        d="M6 4L10 8L6 12"
        stroke={active ? "white" : "#222222"}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Shared form field primitives
// ---------------------------------------------------------------------------

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label
      className="text-xs font-normal text-[#222222] leading-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {children}
    </label>
  );
}

/** Required-field marker, consistent with other dashboard views. */
const REQ = <span className="text-red-500 ml-0.5">*</span>;

function TextInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222]"
      style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
    />
  );
}

function TextArea({
  value,
  onChange,
  placeholder,
  rows = 4,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222] resize-none"
      style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
    />
  );
}

// ---------------------------------------------------------------------------
// Status of FC Failure form (FC Fail → sub-step 1)
// ---------------------------------------------------------------------------

type StatusOfFCFailureFormState = {
  dateOfFCNotice: Date | null;
  lastDateAuto: Date | null;
  reasonForFailure: string;
};

/** Last Date for FC Reply = Date of FC Notice + 30 days. */
function fcReplyDueDate(noticeDate: Date | null): Date | null {
  if (!noticeDate) return null;
  const due = new Date(noticeDate);
  due.setDate(due.getDate() + 30);
  return due;
}

function StatusOfFCFailureForm({
  value,
  onChange,
}: {
  value: StatusOfFCFailureFormState;
  onChange: (v: StatusOfFCFailureFormState) => void;
}) {
  return (
    <div
      className="flex flex-col gap-4"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Date of FC Notice + Last Date (Auto) */}
      <div className="flex flex-row gap-4">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <FieldLabel>Date of FC Notice{REQ}</FieldLabel>
          <DateTimePicker
            value={value.dateOfFCNotice}
            onChange={(d) =>
              onChange({
                ...value,
                dateOfFCNotice: d,
                // Auto-fill Last Date for FC Reply = FC Notice Date + 30 days
                // (still editable below).
                lastDateAuto: fcReplyDueDate(d),
              })
            }
            placeholder="Select Date"
            buttonClassName="w-full h-9 flex items-center justify-between px-2.5 py-2 text-xs text-[#6D6D6D] border border-[#EEEEEE] rounded-[6px] bg-white hover:bg-[#F7F7F7] focus:outline-none"
          />
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <FieldLabel>Last Date for FC Reply{REQ}</FieldLabel>
          <DateTimePicker
            value={value.lastDateAuto}
            onChange={(d) => onChange({ ...value, lastDateAuto: d })}
            placeholder="Select Date"
            buttonClassName="w-full h-9 flex items-center justify-between px-2.5 py-2 text-xs text-[#6D6D6D] border border-[#EEEEEE] rounded-[6px] bg-white hover:bg-[#F7F7F7] focus:outline-none"
          />
        </div>
      </div>

      {/* Reason for Failure */}
      <div className="flex flex-col gap-1">
        <FieldLabel>Reason for Failure{REQ}</FieldLabel>
        <TextArea
          value={value.reasonForFailure}
          onChange={(v) => onChange({ ...value, reasonForFailure: v })}
          placeholder="Reason"
          rows={4}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Notice to Client panel (FC Fail → sub-step 2)
// ---------------------------------------------------------------------------

/**
 * Figma: node-id=168-6096
 * Lists the uploaded Notice of FC Fail PDF(s). Tick the applications to send to
 * (one for an individual notice, multiple to send together — the backend combines
 * them); each row has a Preview button. Send via dashboard footer (Send & Continue)
 * or Resend when already sent.
 */
function NoticeToClientPanel({
  phone,
  noticeFiles,
  selectedTms,
  onToggleTm,
  onToggleAll,
  onPreview,
  hasSentNotice,
  canSendNotice,
  resending,
  onResend,
}: {
  phone: string;
  noticeFiles: { tmNo: string; url: string; name: string; mark?: string }[];
  selectedTms: string[];
  onToggleTm: (tmNo: string, checked: boolean) => void;
  onToggleAll: (checked: boolean) => void;
  onPreview: (file: { url: string; name: string }) => void;
  hasSentNotice: boolean;
  canSendNotice: boolean;
  resending: boolean;
  onResend: () => void;
}) {
  const allChecked =
    noticeFiles.length > 0 && noticeFiles.every((f) => selectedTms.includes(f.tmNo));
  return (
    <div
      className="flex flex-col gap-2.5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex items-center justify-between gap-3">
        <span
          className="text-sm font-normal text-[#222222]"
          style={{ lineHeight: "20px" }}
        >
          Message Preview
        </span>
        {noticeFiles.length > 1 && (
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <Checkbox
              checked={allChecked}
              onCheckedChange={(c) => onToggleAll(Boolean(c))}
            />
            <span className="text-xs text-[#6D6D6D]" style={{ lineHeight: "20px" }}>
              Select all
            </span>
          </label>
        )}
      </div>

      <div className="border border-[#EEEEEE] rounded-xl overflow-hidden">
        {noticeFiles.length === 0 ? (
          <div className="px-4 py-4 min-h-[60px] flex items-center">
            <span
              className="text-xs text-[#6D6D6D]"
              style={{ lineHeight: "20px" }}
            >
              No Notice PDF available.
            </span>
          </div>
        ) : (
          <div className="px-3 py-3 flex flex-col gap-2.5">
            {noticeFiles.map((f) => {
              const checked = selectedTms.includes(f.tmNo);
              return (
                <div
                  key={f.tmNo}
                  className="flex items-center gap-3 rounded-[10px] border border-[#EEEEEE] bg-[#F7F7F7] px-3 py-2.5"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(c) => onToggleTm(f.tmNo, Boolean(c))}
                    aria-label={`Include ${f.name}`}
                  />
                  <div className="flex flex-1 min-w-0 flex-col">
                    <span
                      className="truncate text-xs font-medium text-[#222222]"
                      style={{ lineHeight: "20px" }}
                      title={f.mark ? `${f.mark} — ${f.name}` : f.name}
                    >
                      {f.name}
                    </span>
                    <span
                      className="truncate text-[11px] text-[#6D6D6D]"
                      style={{ lineHeight: "16px" }}
                    >
                      TM No. {f.tmNo}
                      {f.mark ? ` · ${f.mark}` : ""}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="flex-shrink-0 px-3 py-1.5 rounded-lg border border-[#D0D0D0] bg-white text-xs font-medium text-[#2E3037] hover:bg-[#F5F5F5]"
                    onClick={() => onPreview({ url: f.url, name: f.name })}
                  >
                    Preview
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="border-t border-[#EEEEEE] flex items-center gap-3 px-3 py-2.5">
          <div className="flex-1 min-w-0 h-9 flex items-center px-2.5 rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7]">
            <span
              className="text-xs text-[#222222] truncate"
              style={{ lineHeight: "20px" }}
            >
              {phone || "(No phone number)"}
            </span>
          </div>
        </div>
      </div>

      {!hasSentNotice && !canSendNotice && (
        <p className="text-xs text-[#6D6D6D] leading-5">
          Upload Notice of Formality Check Fail PDFs in &quot;Status of FC Failure&quot; before
          sending.
        </p>
      )}

      {hasSentNotice && (
        <div className="flex flex-row items-center gap-3 w-full">
          <button
            type="button"
            disabled={resending}
            onClick={onResend}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resending ? "Resending…" : "Resend to WhatsApp"}
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inform Client panel (FC Fail → sub-step 3: Inform Client, Substep 1B(iii))
// ---------------------------------------------------------------------------

/**
 * Figma: node-id=168-6233
 * Monthly Trademark Update preview; send via dashboard footer (Send & Continue).
 */
function InformClientPanel({
  leadId,
  phone,
  hasSentInform,
  resending,
  onResend,
}: {
  leadId: string | null | undefined;
  phone: string;
  hasSentInform: boolean;
  resending: boolean;
  onResend: () => void;
}) {
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [messagePreview, setMessagePreview] = useState<string | null>(null);

  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    setLoadingPreview(true);
    setMessagePreview(null);
    getFcFailInformClientPreview(leadId)
      .then(({ message }) => {
        if (!cancelled) setMessagePreview(message);
      })
      .catch((err) => {
        if (!cancelled)
          toast.error(
            err instanceof Error ? err.message : "Failed to load message preview"
          );
      })
      .finally(() => {
        if (!cancelled) setLoadingPreview(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  return (
    <div
      className="flex flex-col gap-2.5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <span
        className="text-sm font-normal text-[#222222]"
        style={{ lineHeight: "20px" }}
      >
        Message Preview
      </span>

      <div className="border border-[#EEEEEE] rounded-xl overflow-hidden">
        <div className="px-4 py-4 min-h-[80px]">
          {loadingPreview ? (
            <span className="text-xs text-[#6D6D6D]" style={{ lineHeight: "20px" }}>
              Loading preview…
            </span>
          ) : messagePreview ? (
            <p
              className="text-xs text-[#222222] whitespace-pre-line"
              style={{ lineHeight: "20px" }}
            >
              {renderWhatsAppFormatting(messagePreview)}
            </p>
          ) : (
            <span className="text-xs text-[#6D6D6D]" style={{ lineHeight: "20px" }}>
              No preview available
            </span>
          )}
        </div>

        <div className="border-t border-[#EEEEEE] flex items-center gap-3 px-3 py-2.5">
          <div className="flex-1 min-w-0 h-9 flex items-center px-2.5 rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7]">
            <span
              className="text-xs text-[#222222] truncate"
              style={{ lineHeight: "20px" }}
            >
              {phone || "(No phone number)"}
            </span>
          </div>
        </div>
      </div>

      {hasSentInform && leadId && (
        <div className="flex flex-row items-center gap-3 w-full">
          <button
            type="button"
            disabled={resending || loadingPreview}
            onClick={onResend}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resending ? "Resending…" : "Resend to WhatsApp"}
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Call Client panel (FC Fail → Substep 1B(v)) — node-id=168-6567
// ---------------------------------------------------------------------------

/**
 * Substep 1B(v): Phone call to the client — nothing to enter.
 * Figma: node-id=168-6567
 * Warm peach card with dashed border, headset avatar, descriptive text,
 * and a "Call Completed" button that records the step via the API.
 */
function CallClientPanel({
  leadId,
  recorded,
  onRecorded,
}: {
  leadId: string | null | undefined;
  phone: string;
  recorded: boolean;
  onRecorded: () => void;
}) {
  const [recording, setRecording] = useState(false);

  const handleRecord = async () => {
    if (!leadId || recording) return;
    setRecording(true);
    try {
      await recordCallClientFcFail(leadId);
      onRecorded();
      toast.success("Call Client step recorded successfully.");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to record call client step"
      );
    } finally {
      setRecording(false);
    }
  };

  return (
    <div style={{ fontFamily: "Inter, sans-serif" }}>
      {/* Peach card with dashed orange border — matches Figma node-id=168-6567 */}
      <div
        className="flex flex-row items-center gap-4 p-4 rounded-xl"
        style={{
          background: "#FFF8EE",
          border: "1.5px dashed #F5A623",
          borderRadius: "12px",
        }}
      >
        {/* Customer service avatar */}
        <img
          src={legalAdviceHeadset}
          alt=""
          className="w-[60px] h-[60px] flex-shrink-0"
        />

        {/* Text + action */}
        <div className="flex flex-col gap-3 flex-1 min-w-0">
          <p
            className="text-sm font-normal text-[#222222]"
            style={{ lineHeight: "20px" }}
          >
            Call the client to explain the FC Failure and the need for a reply.
          </p>

          {recorded ? (
            <div className="flex items-center gap-2">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="flex-shrink-0"
              >
                <circle cx="8" cy="8" r="7.5" fill="#222222" />
                <path
                  d="M5.33325 8.00008L7.33325 10.0001L10.6666 6.00008"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span
                className="text-xs font-semibold text-[#222222]"
                style={{ lineHeight: "20px" }}
              >
                Call Completed
              </span>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleRecord}
              disabled={recording || !leadId}
              className="self-start h-8 px-4 text-xs font-medium text-[#222222] bg-white border border-[#D1D5DB] rounded-md hover:bg-[#F9FAFB] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              style={{ lineHeight: "20px" }}
            >
              {recording ? "Recording…" : "Call Completed"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FC Fail Invoice panel (Substep 1B(iv)) — node-id=168-6428
// ---------------------------------------------------------------------------

type FcReplyPreviewTab = "summary" | "pdf" | "message" | "email";

/** Convert "DD-MM-YYYY" or "YYYY-MM-DD" to a Date object. */
function parseFcInvoiceDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const iso = s.includes("-") && s.length === 10 && s.indexOf("-") === 4
    ? s
    : s.split("-").reverse().join("-");
  const d = new Date(iso + "T12:00:00");
  return isNaN(d.getTime()) ? null : d;
}

function formatDateDDMMYYYY(d: Date | null): string {
  if (!d) return "—";
  return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
}

function formatFcRupees(val: string): string {
  const n = parseFloat(val);
  if (isNaN(n) || val === "") return "—";
  return `Rs.${Math.round(n).toLocaleString("en-IN")}/-`;
}

/** Format a Date to "YYYY-MM-DD" for API payloads. */
function toIsoDate(d: Date | null): string | undefined {
  if (!d) return undefined;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function FcPreviewRow({
  label,
  children,
  alignTop = false,
}: {
  label: string;
  children: React.ReactNode;
  alignTop?: boolean;
}) {
  const align = alignTop ? "items-start" : "items-baseline";
  return (
    <>
      <div className={`flex ${align} font-medium text-[#222]`}>{label}</div>
      <div className={`flex ${align} text-[#222]`}>:</div>
      <div className="text-[#222] break-words min-w-0">{children}</div>
    </>
  );
}

/**
 * Spec pg 69 ("Data : Invoice for FC Reply") is a **single** window ordered
 * Addressee → per-application details (TM No., Date of Filing, Mark (Type), Class, Reason for FC
 * Failure) → Last date for FC Reply → Invoice Amount → Date of Invoice.
 *
 * Review item 36: every one of those fields already existed, but the application details sat in a
 * *separate* section rendered **after** the amounts, so the block the client was reading — the one
 * titled "Invoice details" — appeared to be missing them. The per-application table is now rendered
 * inline, in spec position, so the window reads in document order.
 *
 * The table presentation is kept in place of the spec's repeated label:value blocks — it carries the
 * same fields and stays readable when several applications share the acknowledgement.
 */
function FcReplyInvoiceDataPreview({
  designation,
  addresseeLine1,
  addresseeLine2,
  addresseeLine3,
  applications,
  lastDateReply,
  invoiceAmount,
  invoiceDate,
  invoiceNo,
}: {
  designation: string;
  addresseeLine1: string;
  addresseeLine2: string;
  addresseeLine3: string;
  applications: FcReplyInvoicePreview["applications"];
  lastDateReply: Date | null;
  invoiceAmount: string;
  invoiceDate: Date | null;
  invoiceNo: string;
}) {
  const addresseeLines = [addresseeLine1, addresseeLine2, addresseeLine3]
    .map((l) => (typeof l === "string" ? l.trim() : ""))
    .filter(Boolean);
  const gridClass =
    "grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5";
  return (
    <div className="flex flex-col gap-3">
      <div className={gridClass} style={{ fontFamily: "Inter, sans-serif" }}>
        <FcPreviewRow label="Form of Address">
          {designation?.trim() || <span className="text-[#6D6D6D]">—</span>}
        </FcPreviewRow>
        <FcPreviewRow label="Addressee" alignTop>
          {addresseeLines.length > 0 ? (
            <div className="flex flex-col">
              {addresseeLines.map((line, i) => (
                <span key={i}>{line}</span>
              ))}
            </div>
          ) : (
            <span className="text-[#6D6D6D]">—</span>
          )}
        </FcPreviewRow>
      </div>

      <FcReplyApplicationsTable applications={applications} />

      <div className={gridClass} style={{ fontFamily: "Inter, sans-serif" }}>
        <FcPreviewRow label="Last Date for FC Reply">
          {lastDateReply ? (
            formatDateDDMMYYYY(lastDateReply)
          ) : (
            <span className="text-[#6D6D6D]">—</span>
          )}
        </FcPreviewRow>
        <FcPreviewRow label="Invoice Amount for FC Reply">
          {formatFcRupees(invoiceAmount)}
        </FcPreviewRow>
        <FcPreviewRow label="Date of Invoice for FC Reply">
          {invoiceDate ? (
            formatDateDDMMYYYY(invoiceDate)
          ) : (
            <span className="text-[#6D6D6D]">—</span>
          )}
        </FcPreviewRow>
        <FcPreviewRow label="Invoice No. for FC Reply">
          {invoiceNo?.trim() || (
            <span className="text-[#6D6D6D]">Auto-generated on save</span>
          )}
        </FcPreviewRow>
      </div>
    </div>
  );
}

function FcReplyApplicationsTable({
  applications,
}: {
  applications: FcReplyInvoicePreview["applications"];
}) {
  return (
    <div className="overflow-x-auto rounded-[6px] border border-[#EEEEEE]">
      <table className="w-full border-collapse text-[12px] leading-5">
        <thead className="bg-[#F7F7F7]">
          <tr>
            <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE] whitespace-nowrap">
              Application Number
            </th>
            <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE]">
              Class
            </th>
            <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE]">
              Mark
            </th>
            <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE]">
              Type of Mark
            </th>
            <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE] whitespace-nowrap">
              Date of Filing
            </th>
            <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE]">
              Reason for FC Failure
            </th>
          </tr>
        </thead>
        <tbody>
          {applications.length === 0 ? (
            <tr>
              <td
                colSpan={6}
                className="px-2.5 py-3 text-center text-[#6D6D6D]"
              >
                No applications.
              </td>
            </tr>
          ) : (
            applications.map((app, idx) => (
              <tr
                key={`${app.tmNo}-${idx}`}
                className={
                  idx < applications.length - 1
                    ? "border-b border-[#EEEEEE]"
                    : ""
                }
              >
                <td className="px-2.5 py-2 text-[#222222] align-top whitespace-nowrap">
                  {app.tmNo?.trim() || "—"}
                </td>
                <td className="px-2.5 py-2 text-[#222222] align-top">
                  {app.classNumber != null ? String(app.classNumber) : "—"}
                </td>
                <td className="px-2.5 py-2 text-[#222222] align-top">
                  {app.mark?.trim() || "—"}
                </td>
                <td className="px-2.5 py-2 text-[#222222] align-top">
                  {app.typeOfMark?.trim() || "—"}
                </td>
                <td className="px-2.5 py-2 text-[#222222] align-top whitespace-nowrap">
                  {formatDateDDMMYYYY(parseFcInvoiceDate(app.applicationDate))}
                </td>
                <td className="px-2.5 py-2 text-[#222222] align-top">
                  {app.reasonForFcFailure?.trim() || "—"}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

/** Item 58: map every TM to its own generated invoice PDF URL (no cross-TM fallback). */
function buildFcReplyPdfUrlByTm(
  status: FcReplyInvoiceStatus
): Record<string, string | null> {
  const map: Record<string, string | null> = {};
  for (const row of status.tmInvoiceRows ?? []) {
    map[row.tmNo] = row.fcReplyInvoicePdfUrl ?? null;
  }
  return map;
}

function FcReplyInvoiceTabsPanel({
  previewApps,
  designation,
  addresseeLine1,
  addresseeLine2,
  addresseeLine3,
  lastDateReply,
  invoiceAmount,
  invoiceDate,
  invoiceNo,
  pdfUrl,
  pdfFilename,
  pdfGenerating = false,
  previewTab,
  setPreviewTab,
  numPages,
  setNumPages,
  paymentMessage,
  paymentMessageLoading,
  emailPreview,
  embedInDialog = true,
}: {
  previewApps: FcReplyInvoicePreview["applications"];
  designation: string;
  addresseeLine1: string;
  addresseeLine2: string;
  addresseeLine3: string;
  lastDateReply: Date | null;
  invoiceAmount: string;
  invoiceDate: Date | null;
  invoiceNo: string;
  pdfUrl: string | null;
  pdfFilename?: string | null;
  pdfGenerating?: boolean;
  previewTab: FcReplyPreviewTab;
  setPreviewTab: (tab: FcReplyPreviewTab) => void;
  numPages: number | null;
  setNumPages: (n: number | null) => void;
  paymentMessage: string | null;
  paymentMessageLoading: boolean;
  emailPreview: FcReplyInvoiceEmailPreview | null;
  embedInDialog?: boolean;
}) {
  const pageWidth = Math.min(
    800,
    typeof window !== "undefined" ? window.innerWidth - 48 : 800
  );

  return (
    <div className={`flex min-h-0 flex-col ${embedInDialog ? "" : "h-full min-h-0"}`}>
      <div className="flex shrink-0 gap-1 border-b border-[#e3e3e3] bg-white px-2 sm:px-3" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={previewTab === "summary"}
          onClick={() => setPreviewTab("summary")}
          className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
            previewTab === "summary"
              ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
              : "text-[#6d6d6d] hover:text-[#222]"
          }`}
        >
          Invoice details
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={previewTab === "pdf"}
          onClick={() => setPreviewTab("pdf")}
          disabled={!pdfUrl && !pdfGenerating}
          className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation disabled:cursor-not-allowed disabled:opacity-50 ${
            previewTab === "pdf"
              ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
              : "text-[#6d6d6d] hover:text-[#222]"
          }`}
        >
          Generated PDF{pdfGenerating ? "…" : ""}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={previewTab === "message"}
          onClick={() => setPreviewTab("message")}
          className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
            previewTab === "message"
              ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
              : "text-[#6d6d6d] hover:text-[#222]"
          }`}
        >
          WhatsApp Message
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={previewTab === "email"}
          onClick={() => setPreviewTab("email")}
          className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
            previewTab === "email"
              ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
              : "text-[#6d6d6d] hover:text-[#222]"
          }`}
        >
          Email
        </button>
      </div>
      <div
        className={
          embedInDialog
            ? "max-h-[55vh] min-h-0 flex-1 overflow-auto bg-[#fafafa] p-2 sm:p-3 md:max-h-[min(480px,55vh)]"
            : "min-h-0 flex-1 overflow-auto bg-[#fafafa] p-2 sm:p-4"
        }
      >
        {previewTab === "summary" ? (
          <div
            className="flex w-full max-w-3xl mx-auto flex-col gap-3 font-inter"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Data : Invoice for FC Reply
              </p>
              <FcReplyInvoiceDataPreview
                designation={designation}
                addresseeLine1={addresseeLine1}
                addresseeLine2={addresseeLine2}
                addresseeLine3={addresseeLine3}
                applications={previewApps}
                lastDateReply={lastDateReply}
                invoiceAmount={invoiceAmount}
                invoiceDate={invoiceDate}
                invoiceNo={invoiceNo}
              />
            </section>
          </div>
        ) : previewTab === "pdf" ? (
          pdfUrl ? (
            <Document
              file={pdfUrl}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              loading={<p className="text-sm text-[#6d6d6d] font-inter">Loading PDF…</p>}
              error={<p className="text-sm text-red-600 font-inter">Failed to load PDF.</p>}
              className="flex flex-col items-center gap-4"
            >
              {numPages != null &&
                Array.from({ length: numPages }, (_, i) => (
                  <Page
                    key={i + 1}
                    pageNumber={i + 1}
                    width={pageWidth}
                    renderTextLayer
                    renderAnnotationLayer
                  />
                ))}
            </Document>
          ) : pdfGenerating ? (
            <p className="text-sm text-[#6d6d6d] font-inter">Generating PDF…</p>
          ) : (
            <p className="text-sm text-[#6d6d6d] font-inter">No PDF URL available.</p>
          )
        ) : previewTab === "message" ? (
          <div className="rounded-lg border border-[#EEEEEE] bg-white p-4 shadow-sm sm:p-5 font-inter">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
              Will be sent to client WhatsApp after the invoice PDF
            </p>
            <WhatsAppMessageBlock
              message={paymentMessage ?? ""}
              loading={paymentMessageLoading}
              emptyText="Payment message preview not available."
            />
          </div>
        ) : (
          <div className="rounded-lg border border-[#EEEEEE] bg-white p-4 shadow-sm sm:p-5 font-inter">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
              Will be emailed to the client with the invoice PDF
            </p>
            <EmailPreviewBlock
              preview={emailPreview}
              loading={paymentMessageLoading}
              attachmentFallback={
                pdfUrl
                  ? {
                      filename:
                        pdfFilename ?? "Invoice_for_Reply_to_Formality_Check.pdf",
                      url: pdfUrl,
                    }
                  : null
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}

type FcFailInvoicePanelHandle = { save: () => Promise<void> };

const FcFailInvoicePanel = forwardRef<
  FcFailInvoicePanelHandle,
  {
    leadId: string | null | undefined;
    phone: string;
    hasSentInvoice: boolean;
    resending: boolean;
    onResend: () => void;
    onInvoiceSendReadyChange?: (ready: boolean) => void;
    /**
     * Parent-controlled TM selection (sourced from the mark pills above the panel).
     * Drives which TM's invoice fields the panel renders and edits.
     */
    selectedTmNo?: string;
  }
>(function FcFailInvoicePanel(
  {
    leadId,
    phone,
    hasSentInvoice,
    resending,
    onResend,
    onInvoiceSendReadyChange,
    selectedTmNo: selectedTmNoFromParent,
  },
  ref,
) {
  // Per-TM in-memory state. Each TM has its own field values so switching
  // pills doesn't leak data between TMs, and unsaved edits survive switching.
  type TmFieldState = {
    mark: string;
    classNo: string;
    typeOfMark: string;
    filingDate: Date | null;
    invoiceAmount: string;
    invoiceDate: Date | null;
    invoiceNo: string;
  };
  const EMPTY_TM_FIELDS: TmFieldState = {
    mark: "",
    classNo: "",
    typeOfMark: "",
    filingDate: null,
    invoiceAmount: "",
    invoiceDate: null,
    invoiceNo: "",
  };

  const [tmFields, setTmFields] = useState<Record<string, TmFieldState>>({});
  const [internalSelectedTmNo, setSelectedTmNo] = useState<string>("");
  // Parent (mark pills above the panel) is the source of truth when provided;
  // fall back to the panel's own state for single-TM or pre-parent-wiring flows.
  const selectedTmNo = selectedTmNoFromParent?.trim() || internalSelectedTmNo;

  // Lead-level state (shared across TMs)
  const [ackNo, setAckNo] = useState<string>("");
  // Whether the auto-generated invoice number is printed on the PDF.
  const [includeInvoiceNo, setIncludeInvoiceNo] = useState(false);
  const [lastDateReply, setLastDateReply] = useState<Date | null>(null);
  const [designation, setDesignation] = useState<string>("");
  const [addresseeLine1, setAddresseeLine1] = useState<string>("");
  const [addresseeLine2, setAddresseeLine2] = useState<string>("");
  const [addresseeLine3, setAddresseeLine3] = useState<string>("");

  // Cached preview list for the TM selector + multi-TM iteration on save.
  const [previewApps, setPreviewApps] = useState<FcReplyInvoicePreview["applications"]>([]);

  // Derived view of the currently-selected TM's fields. JSX reads through these
  // local names so the rest of the component doesn't need to change.
  const currentFields = tmFields[selectedTmNo] ?? EMPTY_TM_FIELDS;
  const { filingDate, invoiceAmount, invoiceDate, invoiceNo } = currentFields;

  // Patch helpers — writes always target the currently-selected TM.
  const patchCurrentTm = (patch: Partial<TmFieldState>) => {
    setTmFields((prev) => {
      if (!selectedTmNo) return prev;
      return {
        ...prev,
        [selectedTmNo]: { ...(prev[selectedTmNo] ?? EMPTY_TM_FIELDS), ...patch },
      };
    });
  };
  const setInvoiceAmount = (val: string) => patchCurrentTm({ invoiceAmount: val });
  const setInvoiceDate = (val: Date | null) => patchCurrentTm({ invoiceDate: val });

  // Action state
  const [loading, setLoading] = useState(!!leadId);
  const [generating, setGenerating] = useState(false);
  /**
   * Review item 58 — the generated PDF is **per application**, so it must be keyed
   * by TM No. This used to be a single `pdfUrl` resolved as "the first row that has
   * any URL", and it was never re-resolved when the TM selection changed. Generating
   * for the second application therefore appeared to do nothing: the panel kept
   * showing the first application's document. The backend already returns a per-row
   * `fcReplyInvoicePdfUrl` (most-specific generated PDF for that TM, else the stage
   * key), so the selected row's value is used directly with no cross-TM fallback.
   */
  const [pdfUrlByTm, setPdfUrlByTm] = useState<Record<string, string | null>>({});

  // Item 58: the PDF shown/sent always belongs to the selected application.
  const pdfUrl = selectedTmNo ? (pdfUrlByTm[selectedTmNo] ?? null) : null;

  // Preview modal state
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewTab, setPreviewTab] = useState<FcReplyPreviewTab>("summary");
  const [numPages, setNumPages] = useState<number | null>(null);
  const [paymentMessageText, setPaymentMessageText] = useState<string | null>(null);
  const [emailPreview, setEmailPreview] =
    useState<FcReplyInvoiceEmailPreview | null>(null);
  const [paymentMessageLoading, setPaymentMessageLoading] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    onInvoiceSendReadyChange?.(Boolean(pdfUrl) && !!leadId && !loading);
  }, [pdfUrl, leadId, loading, onInvoiceSendReadyChange]);

  useEffect(() => {
    if (!leadId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getFcReplyInvoiceStatus(leadId),
      getFcReplyInvoicePreview(leadId).catch(() => null),
      getAcknowledgementSummary(leadId).catch(() => null),
    ])
      .then(([status, preview, ackSummary]) => {
        if (cancelled) return;

        // Acknowledgement number + lead-level addressee fields
        if (ackSummary) setAckNo(ackSummary.acknowledgementNo ?? "");
        setIncludeInvoiceNo(Boolean(status.includeInvoiceNo));
        if (preview) {
          setPreviewApps(preview.applications);
          setLastDateReply(parseFcInvoiceDate(preview.lastDateForFcReply));
          setDesignation(preview.formOfAddress ?? "");
          setAddresseeLine1(preview.addresseeLine1 ?? preview.addressee ?? "");
          setAddresseeLine2(preview.addresseeLine2 ?? "");
          setAddresseeLine3(preview.addresseeLine3 ?? "");
        }

        // Build the per-TM field map: every TM in the preview gets its own
        // entry, merging the preview's row data with the authoritative saved
        // invoice row from `status.tmInvoiceRows` (status wins for invoice
        // amount/date/no since it reflects the latest persisted value).
        const statusByTm = new Map(
          (status.tmInvoiceRows ?? []).map((r) => [r.tmNo, r])
        );
        const initialMap: Record<string, TmFieldState> = {};
        const previewList = preview?.applications ?? [];
        for (const app of previewList) {
          const saved = statusByTm.get(app.tmNo);
          initialMap[app.tmNo] = {
            mark: app.mark ?? "",
            classNo: app.classNumber ? `Class ${app.classNumber}` : "",
            typeOfMark: app.typeOfMark ?? "",
            filingDate: parseFcInvoiceDate(app.applicationDate),
            invoiceAmount:
              saved?.invoiceAmount != null
                ? String(saved.invoiceAmount)
                : app.invoiceAmount != null
                  ? String(app.invoiceAmount)
                  : "",
            invoiceDate: saved?.invoiceDate
              ? parseFcInvoiceDate(saved.invoiceDate)
              : app.invoiceDate
                ? parseFcInvoiceDate(app.invoiceDate)
                : null,
            invoiceNo:
              saved?.individualInvoiceNo ??
              app.individualInvoiceNo ??
              (previewList.length <= 1 ? (status.invoiceNo ?? "") : ""),
          };
        }
        // Include any status rows whose TM isn't in the preview list (defensive).
        for (const row of status.tmInvoiceRows ?? []) {
          if (initialMap[row.tmNo]) continue;
          initialMap[row.tmNo] = {
            mark: "",
            classNo: "",
            typeOfMark: "",
            filingDate: null,
            invoiceAmount: row.invoiceAmount != null ? String(row.invoiceAmount) : "",
            invoiceDate: row.invoiceDate ? parseFcInvoiceDate(row.invoiceDate) : null,
            invoiceNo: row.individualInvoiceNo ?? "",
          };
        }
        setTmFields(initialMap);
        if (previewList.length > 0) {
          setSelectedTmNo(previewList[0].tmNo);
        }

        setPdfUrlByTm(buildFcReplyPdfUrlByTm(status));
      })
      .catch((err) => {
        if (!cancelled) toast.error(err instanceof Error ? err.message : "Failed to load invoice");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [leadId]);

  // Every TM that must be persisted on save. The selected pill only controls
  // which TM is shown for editing — saving always covers all TMs because the
  // backend replaces the full invoice item set on each save, so scoping to one
  // TM would wipe the others.
  const getAllTmNos = (): string[] => {
    const list = previewApps.map((a) => a.tmNo).filter(Boolean);
    for (const tmNo of Object.keys(tmFields)) {
      if (tmNo && !list.includes(tmNo)) list.push(tmNo);
    }
    return list.length ? list : selectedTmNo ? [selectedTmNo] : [];
  };

  // Invoice Date + Invoice Amount are required before the invoice can be
  // saved/generated/sent. Validates every TM included in the save payload so
  // multi-TM flows can't slip through with a missing date or zero amount.
  // Returns an error message, or null when all required fields are present.
  const validateInvoiceFields = (): string | null => {
    for (const tmNo of getAllTmNos()) {
      const fields = tmFields[tmNo] ?? EMPTY_TM_FIELDS;
      if (!fields.invoiceDate)
        return `Please select an invoice date for TM No. ${tmNo}.`;
      const amt = parseFloat(fields.invoiceAmount);
      if (
        fields.invoiceAmount.trim() === "" ||
        !Number.isFinite(amt) ||
        amt <= 0
      ) {
        return `Please enter a valid invoice amount for TM No. ${tmNo}.`;
      }
    }
    return null;
  };

  const buildSavePayload = () => {
    const tmList = getAllTmNos();
    const headerDate = selectedTmNo
      ? (tmFields[selectedTmNo]?.invoiceDate ?? null)
      : (tmFields[tmList[0]]?.invoiceDate ?? null);
    return {
      invoiceDate: toIsoDate(headerDate),
      includeInvoiceNo,
      applications: tmList.map((tmNo) => {
        const fields = tmFields[tmNo] ?? EMPTY_TM_FIELDS;
        return {
          tmNo,
          invoiceAmount: parseFloat(fields.invoiceAmount) || 0,
          invoiceDate: toIsoDate(fields.invoiceDate),
        };
      }),
    };
  };

  // Merge a saved/refreshed status response into the per-TM map so subsequent
  // TM switches show the latest persisted values (invoice no auto-issued by
  // the backend, normalized invoice amount/date, etc.).
  const mergeStatusIntoMap = (resp: { tmInvoiceRows: FcReplyInvoiceTmRow[]; invoiceNo: string | null }) => {
    setTmFields((prev) => {
      const next = { ...prev };
      for (const row of resp.tmInvoiceRows ?? []) {
        const existing = next[row.tmNo] ?? EMPTY_TM_FIELDS;
        next[row.tmNo] = {
          ...existing,
          ...(row.invoiceAmount != null
            ? { invoiceAmount: String(row.invoiceAmount) }
            : {}),
          ...(row.invoiceDate
            ? { invoiceDate: parseFcInvoiceDate(row.invoiceDate) }
            : {}),
          ...(row.individualInvoiceNo
            ? { invoiceNo: row.individualInvoiceNo }
            : {}),
        };
      }
      return next;
    });
  };

  useImperativeHandle(ref, () => ({
    save: async () => {
      if (!leadId) return;
      const validationError = validateInvoiceFields();
      if (validationError) throw new Error(validationError);
      const saved = await saveFcReplyInvoice(leadId, buildSavePayload());
      mergeStatusIntoMap(saved);
    },
  }));

  const handleGeneratePdf = async () => {
    if (!leadId || generating) return;
    const validationError = validateInvoiceFields();
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setGenerating(true);
    try {
      const saved = await saveFcReplyInvoice(leadId, buildSavePayload());
      mergeStatusIntoMap(saved);

      await generateFcReplyInvoicePdf(
        leadId,
        selectedTmNo ? [selectedTmNo] : undefined
      );

      const refreshed = await getFcReplyInvoiceStatus(leadId);
      setPdfUrlByTm(buildFcReplyPdfUrlByTm(refreshed));
      mergeStatusIntoMap(refreshed);

      toast.success("Invoice PDF generated successfully.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to generate PDF");
    } finally {
      setGenerating(false);
    }
  };

  // Preview UX: open the modal on the Details tab immediately using local state
  // (Invoice details + Applications come from props/state, no server needed). PDF
  // generation, status refresh, and payment/email message preview run in the
  // background; the PDF and Message/Email tabs show their own loading states.
  const handlePreview = async () => {
    if (!leadId || previewing) return;
    const validationError = validateInvoiceFields();
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setPreviewing(true);

    // Open the modal right away on Details — fields render from local state.
    setPreviewTab("summary");
    setNumPages(null);
    setPaymentMessageText(null);
    setEmailPreview(null);
    setPaymentMessageLoading(true);
    setGenerating(true);
    setPreviewModalOpen(true);

    try {
      // Quick save first so invoiceNo/date are persisted before the message
      // preview is built; the Details tab already shows current local state.
      const saved = await saveFcReplyInvoice(leadId, buildSavePayload());
      mergeStatusIntoMap(saved);

      // PDF generation + status refresh + message-preview fetch all in the
      // background. Modal stays open and responsive throughout.
      void (async () => {
        try {
          await generateFcReplyInvoicePdf(
            leadId,
            selectedTmNo ? [selectedTmNo] : undefined,
          );
          const refreshed = await getFcReplyInvoiceStatus(leadId);
          setPdfUrlByTm(buildFcReplyPdfUrlByTm(refreshed));
          mergeStatusIntoMap(refreshed);

          // Email attachment + payment message depend on a freshly-generated
          // PDF, so fetch after generation completes. Scope to the selected TM
          // so the WhatsApp Message amount + email attachment match the per-TM
          // PDF generated above (otherwise the message shows the aggregate of
          // all TMs).
          try {
            const { message, emailPreview: email } =
              await getFcReplyInvoicePaymentMessagePreview(
                leadId,
                selectedTmNo ? { tmNos: [selectedTmNo] } : undefined,
              );
            setPaymentMessageText(message);
            setEmailPreview(email ?? null);
          } catch {
            setPaymentMessageText(null);
            setEmailPreview(null);
          }
        } catch (err) {
          toast.error(
            err instanceof Error ? err.message : "Failed to generate PDF",
          );
        } finally {
          setGenerating(false);
          setPaymentMessageLoading(false);
        }
      })();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save invoice");
      setGenerating(false);
      setPaymentMessageLoading(false);
    } finally {
      setPreviewing(false);
    }
  };

  if (!leadId) {
    return (
      <p className="text-xs text-[#6D6D6D]" style={{ fontFamily: "Inter, sans-serif" }}>
        Please select a lead to access this form.
      </p>
    );
  }

  if (loading) {
    return (
      <p className="text-xs text-[#6D6D6D]" style={{ fontFamily: "Inter, sans-serif" }}>
        Loading invoice…
      </p>
    );
  }

  const labelCls = "text-[12px] font-normal leading-5 text-[#222222]";
  const readonlyCls =
    "h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7] px-2.5 py-2 text-[12px] leading-5 text-[#222222] box-border flex items-center truncate";
  const readonlyPlaceholderCls = "text-[#6D6D6D]";
  const dateBtnCls =
    "!h-9 !min-h-[44px] md:!min-h-0 !text-[12px] rounded-[6px]";

  const formattedInvoiceAmount = (() => {
    const n = parseFloat(invoiceAmount);
    if (!Number.isFinite(n) || invoiceAmount === "") return "Rs. 0/-";
    return `Rs. ${Math.round(n).toLocaleString("en-IN")}/-`;
  })();

  return (
    <div
      className="flex w-full max-w-[1085px] flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Row 1: Acknowledgement Number | Filing Date | Last Date Reply */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={labelCls}>Acknowledgement Number</label>
          <div className={readonlyCls}>
            {ackNo || <span className={readonlyPlaceholderCls}>—</span>}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label className={labelCls}>Filing Date</label>
          <DateTimePicker
            value={filingDate}
            onChange={() => {}}
            placeholder="Select Date"
            readOnly
            buttonClassName={dateBtnCls + " bg-[#F7F7F7] text-[#222222] cursor-default"}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={labelCls}>Last Date Reply</label>
          <DateTimePicker
            value={lastDateReply}
            onChange={() => {}}
            placeholder="Select Date"
            readOnly
            buttonClassName={dateBtnCls + " bg-[#F7F7F7] text-[#222222] cursor-default"}
          />
        </div>
      </div>

      {/* Row 2: Invoice Date | Invoice Amount | Invoice Number */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={labelCls}>
            Invoice Date<span className="text-red-500 ml-0.5">*</span>
          </label>
          <DateTimePicker
            value={invoiceDate}
            onChange={setInvoiceDate}
            placeholder="Select Date"
            buttonClassName={dateBtnCls + " bg-white text-[#6D6D6D] hover:bg-[#F7F7F7]"}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={labelCls}>
            Invoice Amount<span className="text-red-500 ml-0.5">*</span>
          </label>
          <div className="flex h-9 min-h-[44px] md:min-h-0 box-border border border-[#EEEEEE] rounded-[6px] bg-white overflow-hidden focus-within:ring-1 focus-within:ring-[#222]">
            <input
              type="text"
              inputMode="decimal"
              value={invoiceAmount}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^\d.]/g, "");
                const parts = raw.split(".");
                const v =
                  parts.length > 1
                    ? `${parts[0]}.${parts.slice(1).join("")}`
                    : raw;
                setInvoiceAmount(v);
              }}
              placeholder="00.00"
              className="flex-1 min-w-0 h-full px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:outline-none bg-transparent"
            />
            <div className="flex items-center gap-0.5 px-2.5 border-l border-[#EEEEEE] text-[12px] text-[#222222] shrink-0">
              Rs.(₹)
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                className="ml-0.5 opacity-40"
              >
                <path
                  d="M2.5 3.75L5 6.25L7.5 3.75"
                  stroke="#222222"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label className={labelCls}>Invoice Number</label>
          <div className={readonlyCls}>
            {invoiceNo || (
              <span className={readonlyPlaceholderCls}>Auto-generated on save</span>
            )}
          </div>
        </div>
      </div>

      {/* Invoice Amount summary bar — matches reference (FollowUp Invoice) */}
      <div
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-3 rounded-[8px] px-4 sm:px-5 py-4 sm:py-0 min-h-[72px] sm:h-[72px]"
        style={{ background: "#222222" }}
      >
        <div className="flex flex-row items-center gap-2 min-w-0">
          <img
            src={invoiceAmountCheck}
            alt=""
            className="w-5 h-5 flex-shrink-0"
          />
          <span className="text-[12px] md:text-sm font-normal leading-5 text-white/90 truncate">
            INVOICE AMOUNT FOR FC REPLY
          </span>
        </div>
        <span className="text-lg md:text-[22px] font-bold leading-8 md:leading-10 text-white shrink-0">
          {formattedInvoiceAmount}
        </span>
      </div>

      {/* Include Invoice No. toggle — controls whether the invoice number prints on the PDF */}
      <div className="flex items-center gap-2">
        <Checkbox
          id="fc-fail-include-invoice-no"
          checked={includeInvoiceNo}
          onCheckedChange={(checked) => setIncludeInvoiceNo(Boolean(checked))}
        />
        <label
          htmlFor="fc-fail-include-invoice-no"
          className="text-xs md:text-sm text-[#222222] cursor-pointer"
        >
          Include Invoice No. in PDF
        </label>
      </div>

      {/* Generate PDF + Download + Preview */}
      <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5">
        <div className="flex flex-row items-center gap-2.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] pl-4 pr-2 py-2 min-h-[44px] sm:h-11">
          <img
            src={invoiceGenerate}
            alt=""
            className="w-[22px] h-[22px] flex-shrink-0"
          />
          <button
            type="button"
            onClick={handleGeneratePdf}
            disabled={generating || previewing || !leadId}
            className="flex flex-row items-center justify-center min-h-[40px] h-9 px-4 rounded-[6px] bg-[#222222] text-[12px] md:text-xs font-medium text-white hover:bg-[#333333] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
          >
            {generating
              ? "Generating..."
              : pdfUrl
                ? "Regenerate PDF"
                : "Generate PDF"}
          </button>
        </div>
        {pdfUrl ? (
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] md:text-xs font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 touch-manipulation"
          >
            Download
          </a>
        ) : (
          <button
            type="button"
            disabled
            className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] md:text-xs font-medium text-[#222222] opacity-40 shrink-0 cursor-not-allowed"
          >
            Download
          </button>
        )}
        <button
          type="button"
          onClick={handlePreview}
          disabled={previewing || generating || !leadId}
          className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] md:text-xs font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
        >
          {previewing ? "Generating…" : "Preview"}
        </button>
      </div>

      {/* Phone number + optional Resend */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="tel"
          inputMode="numeric"
          value={phone}
          readOnly
          placeholder="e.g. +919876543210"
          className="w-full min-w-0 sm:w-[200px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
        />
        {hasSentInvoice && leadId && (
          <button
            type="button"
            disabled={resending || !pdfUrl}
            onClick={onResend}
            className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resending ? "Resending…" : "Resend Message"}
          </button>
        )}
      </div>

      {/* Invoice preview modal */}
      {previewModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4">
          <div className="relative flex h-[95vh] sm:h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
            <div className="flex shrink-0 items-center justify-between border-b border-[#e3e3e3] px-3 py-3 sm:px-4">
              <span className="truncate pr-2 text-xs font-medium text-[#222] sm:text-sm font-inter">
                Invoice for FC Reply — Preview
              </span>
              <button
                type="button"
                onClick={() => setPreviewModalOpen(false)}
                className="flex size-10 shrink-0 items-center justify-center rounded text-[#6d6d6d] hover:bg-[#eee] hover:text-[#222] sm:size-8 touch-manipulation"
                aria-label="Close"
              >
                <HiOutlineX className="size-5" strokeWidth={2} />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-hidden p-2 sm:p-4">
              <FcReplyInvoiceTabsPanel
                previewApps={previewApps}
                designation={designation}
                addresseeLine1={addresseeLine1}
                addresseeLine2={addresseeLine2}
                addresseeLine3={addresseeLine3}
                lastDateReply={lastDateReply}
                invoiceAmount={invoiceAmount}
                invoiceDate={invoiceDate}
                invoiceNo={invoiceNo}
                pdfUrl={pdfUrl}
                pdfFilename={emailPreview?.filename ?? null}
                pdfGenerating={generating}
                previewTab={previewTab}
                setPreviewTab={setPreviewTab}
                numPages={numPages}
                setNumPages={setNumPages}
                paymentMessage={paymentMessageText}
                paymentMessageLoading={paymentMessageLoading}
                emailPreview={emailPreview}
                embedInDialog={false}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// FC Fail Inform Client (Done) panel (Substep 1B(ix))
// ---------------------------------------------------------------------------

function InformClientDonePanel({
  leadId,
  phone,
  hasSentDone,
  resending,
  onResend,
  onInformDoneSendReadyChange,
}: {
  leadId: string | null | undefined;
  phone: string;
  hasSentDone: boolean;
  resending: boolean;
  onResend: () => void;
  onInformDoneSendReadyChange?: (ready: boolean) => void;
}) {
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [messagePreview, setMessagePreview] = useState<string | null>(null);

  useEffect(() => {
    onInformDoneSendReadyChange?.(
      Boolean(messagePreview?.trim()) && !!leadId && !loadingPreview
    );
  }, [messagePreview, leadId, loadingPreview, onInformDoneSendReadyChange]);

  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    setLoadingPreview(true);
    setMessagePreview(null);
    getFcReplyFiledInformClientPreview(leadId)
      .then(({ message }) => {
        if (!cancelled) setMessagePreview(message);
      })
      .catch((err) => {
        if (!cancelled)
          toast.error(
            err instanceof Error ? err.message : "Failed to load message preview"
          );
      })
      .finally(() => {
        if (!cancelled) setLoadingPreview(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  return (
    <div
      className="flex flex-col gap-2.5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <span
        className="text-sm font-normal text-[#222222]"
        style={{ lineHeight: "20px" }}
      >
        Message Preview
      </span>

      <div className="border border-[#EEEEEE] rounded-xl overflow-hidden">
        <div className="px-4 py-4 min-h-[80px]">
          {loadingPreview ? (
            <span className="text-xs text-[#6D6D6D]" style={{ lineHeight: "20px" }}>
              Loading preview…
            </span>
          ) : messagePreview ? (
            <p
              className="text-xs text-[#222222] whitespace-pre-line"
              style={{ lineHeight: "20px" }}
            >
              {renderWhatsAppFormatting(messagePreview)}
            </p>
          ) : (
            <span className="text-xs text-[#6D6D6D]" style={{ lineHeight: "20px" }}>
              No preview available
            </span>
          )}
        </div>

        <div className="border-t border-[#EEEEEE] flex items-center gap-3 px-3 py-2.5">
          <div className="flex-1 min-w-0 h-9 flex items-center px-2.5 rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7]">
            <span
              className="text-xs text-[#222222] truncate"
              style={{ lineHeight: "20px" }}
            >
              {phone || "(No phone number)"}
            </span>
          </div>
        </div>
      </div>

      {!hasSentDone && !messagePreview?.trim() && !loadingPreview && leadId && (
        <p className="text-xs text-[#6D6D6D] leading-5">
          Complete filing of reply and ensure preview loads before sending (footer: Send & Continue).
        </p>
      )}

      {hasSentDone && leadId && (
        <div className="flex flex-row items-center gap-3 w-full">
          <button
            type="button"
            disabled={resending || loadingPreview || !messagePreview?.trim()}
            onClick={onResend}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resending ? "Resending…" : "Resend to WhatsApp"}
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FC Fail Filing of Reply panel (Substep 1B(viii))
// ---------------------------------------------------------------------------

type FilingOfReplyTmRow = {
  tmNo: string;
  fileKey: string | null;
  filename: string | null;
  fileUrl: string | null;
  pendingFile: File | null;
};

type FilingOfReplyPanelHandle = { save: () => Promise<void> };

const FilingOfReplyPanel = forwardRef<FilingOfReplyPanelHandle, { leadId: string | null | undefined }>(
  function FilingOfReplyPanel({ leadId }, ref) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dateOfFcReplyFiled, setDateOfFcReplyFiled] = useState<Date | null>(null);
  const [rows, setRows] = useState<FilingOfReplyTmRow[]>([]);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [preview, setPreview] = useState<
    { url: string; name: string; kind: FilePreviewKind; revokeOnClose?: boolean } | null
  >(null);

  const openRowPreview = (row: FilingOfReplyTmRow) => {
    if (row.pendingFile) {
      setPreview({
        url: URL.createObjectURL(row.pendingFile),
        name: row.pendingFile.name,
        kind: detectPreviewKind(row.pendingFile.name, row.pendingFile.type),
        revokeOnClose: true,
      });
      return;
    }
    if (row.fileUrl) {
      const name = row.filename ?? "Filed reply";
      setPreview({ url: row.fileUrl, name, kind: detectPreviewKind(name) });
    }
  };

  const closePreview = () => {
    if (preview?.revokeOnClose) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  useEffect(() => {
    if (!leadId) return;
    setLoading(true);
    getFcReplyFiledStatus(leadId)
      .then((data: FcReplyFiledStatus) => {
        setDateOfFcReplyFiled(parseFcInvoiceDate(data.applications[0]?.dateOfFcReplyFiled));
        setRows(
          data.applications.map((a: FcReplyFiledApplication) => ({
            tmNo: a.tmNo,
            fileKey: a.fcReplyFileKey,
            filename: a.fcReplyFilename,
            fileUrl: a.fcReplyFileUrl,
            pendingFile: null,
          }))
        );
      })
      .catch(() => toast.error("Failed to load filing of reply status"))
      .finally(() => setLoading(false));
  }, [leadId]);

  const updateRow = (tmNo: string, patch: Partial<FilingOfReplyTmRow>) => {
    setRows((prev) =>
      prev.map((r) => (r.tmNo === tmNo ? { ...r, ...patch } : r))
    );
  };

  const handleFileDrop = (tmNo: string, file: File) => {
    updateRow(tmNo, { pendingFile: file, filename: file.name });
  };

  const handleFileInput = (tmNo: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileDrop(tmNo, file);
  };

  useImperativeHandle(ref, () => ({
    save: async () => {
      if (!leadId) return;

      // 1B(viii): Upload and Date of FC Reply Filed are both mandatory (review item 18). Previously
      // a missing value was just omitted from the payload and the step still advanced, so a reply
      // could be marked filed with no document and no date. Validate before uploading anything, and
      // throw — the caller advances on resolve and swallows the error, so returning would still
      // move the workflow on.
      const isoDate = toIsoDate(dateOfFcReplyFiled);
      if (!isoDate) {
        toast.error("Date of FC Reply Filed is required");
        throw new Error("Date of FC Reply Filed is required");
      }
      const missingUploads = rows
        .filter((r) => !r.pendingFile && !r.fileKey)
        .map((r) => r.tmNo);
      if (missingUploads.length > 0) {
        toast.error(
          `Upload the filed reply for TM No. ${missingUploads.join(", ")}`
        );
        throw new Error("Filed reply upload is required");
      }

      setSaving(true);
      try {
        const pendingRows = rows.filter((r) => r.pendingFile);

        let uploadedKeys: Record<string, string> = {};
        if (pendingRows.length > 0) {
          const presignedUrls = await generateFcReplyFiledPresignedUrls(
            leadId,
            pendingRows.map((r) => ({
              tmNo: r.tmNo,
              filename: r.pendingFile!.name,
              mimeType: r.pendingFile!.type || "application/pdf",
            }))
          );

          for (const item of presignedUrls) {
            const row = pendingRows.find((r) => r.tmNo === item.tmNo);
            if (!row?.pendingFile) continue;
            const res = await fetch(item.presignedUrl, {
              method: "PUT",
              body: row.pendingFile,
              headers: {
                "Content-Type": row.pendingFile.type || "application/pdf",
              },
            });
            if (!res.ok)
              throw new Error(`Failed to upload file for ${item.tmNo}`);
            uploadedKeys[item.tmNo] = item.fileKey;
          }
        }

        const payload = {
          applications: rows.map((r) => ({
            tmNo: r.tmNo,
            dateOfFcReplyFiled: isoDate,
            ...(uploadedKeys[r.tmNo]
              ? {
                  fcReplyFileKey: uploadedKeys[r.tmNo],
                  fcReplyFilename: r.pendingFile?.name ?? r.filename ?? "",
                }
              : r.fileKey
              ? { fcReplyFileKey: r.fileKey, fcReplyFilename: r.filename ?? "" }
              : {}),
          })),
        };

        const updated = await saveFcReplyFiled(leadId, payload);
        setRows(
          updated.applications.map((a: FcReplyFiledApplication) => ({
            tmNo: a.tmNo,
            fileKey: a.fcReplyFileKey,
            filename: a.fcReplyFilename,
            fileUrl: a.fcReplyFileUrl,
            pendingFile: null,
          }))
        );
        toast.success("Filing of Reply saved");
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to save filing of reply"
        );
        throw err;
      } finally {
        setSaving(false);
      }
    },
  }), [leadId, rows, dateOfFcReplyFiled]);


  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <span className="text-sm text-[#6D6D6D]">Loading…</span>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-5 w-full"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Date of FC Reply Filed */}
      <div className="flex flex-col gap-1.5">
        <label
          className="text-xs font-medium text-[#6D6D6D]"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          Date of FC Reply Filed
        </label>
        <DateTimePicker
          value={dateOfFcReplyFiled}
          onChange={setDateOfFcReplyFiled}
          placeholder="Select Date"
        />
      </div>

      {/* Per-TM file upload rows */}
      {rows.map((row) => {
        const isDragging = dragOver === row.tmNo;
        const hasFile = !!(row.pendingFile || row.fileKey);

        return (
          <div key={row.tmNo} className="flex flex-col gap-3">
            {rows.length > 1 && (
              <p className="text-xs font-semibold text-[#222222]">{row.tmNo}</p>
            )}

            {/* File Upload label */}
            <div className="flex flex-col gap-0.5">
              <p className="text-sm font-medium text-[#222222]">File Upload</p>
              <p className="text-xs text-[#6D6D6D]">
                Choose a file and upload securely to proceed.
              </p>
            </div>

            {/* Drag & drop zone */}
            <div
              className={`flex items-center gap-4 rounded-lg border border-dashed px-4 py-4 cursor-pointer transition-colors ${
                isDragging
                  ? "border-[#222222] bg-[#F5F5F5]"
                  : hasFile
                  ? "border-[#22C55E] bg-[#F0FDF4]"
                  : "border-[#D0D0D0] bg-white"
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(row.tmNo);
              }}
              onDragLeave={() => setDragOver(null)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(null);
                const file = e.dataTransfer.files[0];
                if (file) handleFileDrop(row.tmNo, file);
              }}
              onClick={() =>
                document
                  .getElementById(`fc-reply-file-input-${row.tmNo}`)
                  ?.click()
              }
            >
              {/* File icon */}
              <div className="flex-shrink-0">
                <svg
                  width="36"
                  height="40"
                  viewBox="0 0 36 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M22 1H5C3.9 1 3 1.9 3 3V37C3 38.1 3.9 39 5 39H31C32.1 39 33 38.1 33 37V12L22 1Z"
                    fill="#E2E8F0"
                    stroke="#CBD5E0"
                    strokeWidth="1.5"
                  />
                  <path d="M22 1V12H33" fill="#CBD5E0" stroke="#CBD5E0" strokeWidth="1.5" />
                  <line x1="10" y1="20" x2="26" y2="20" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" />
                  <line x1="10" y1="25" x2="26" y2="25" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" />
                  <line x1="10" y1="30" x2="20" y2="30" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>

              <div className="flex-1 min-w-0">
                {hasFile ? (
                  <p className="text-sm font-medium text-[#16A34A] truncate">
                    {row.pendingFile?.name ?? row.filename ?? "File selected"}
                  </p>
                ) : (
                  <>
                    <p className="text-sm font-medium text-[#222222]">
                      Drag and drop your files
                    </p>
                    <p className="text-xs text-[#6D6D6D] mt-0.5">
                      JPEG, PNG, PDF, and MP4 formats, up to 50MB
                    </p>
                  </>
                )}
              </div>

              {!hasFile && (
                <button
                  type="button"
                  className="flex-shrink-0 px-4 py-2 rounded-lg border border-[#D0D0D0] bg-white text-sm font-medium text-[#222222] hover:bg-[#F5F5F5] transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    document
                      .getElementById(`fc-reply-file-input-${row.tmNo}`)
                      ?.click();
                  }}
                >
                  Select File
                </button>
              )}

              {hasFile && (
                <>
                  <button
                    type="button"
                    className="flex-shrink-0 px-3 py-1.5 rounded-lg border border-[#D0D0D0] bg-white text-xs font-medium text-[#2E3037] hover:bg-[#F5F5F5]"
                    onClick={(e) => {
                      e.stopPropagation();
                      openRowPreview(row);
                    }}
                  >
                    Preview
                  </button>
                  <button
                    type="button"
                    className="flex-shrink-0 px-3 py-1.5 rounded-lg border border-[#D0D0D0] bg-white text-xs font-medium text-[#6D6D6D] hover:bg-[#F5F5F5]"
                    onClick={(e) => {
                      e.stopPropagation();
                      updateRow(row.tmNo, {
                        pendingFile: null,
                        fileKey: null,
                        filename: null,
                        fileUrl: null,
                      });
                    }}
                  >
                    Remove
                  </button>
                </>
              )}
            </div>

            <input
              id={`fc-reply-file-input-${row.tmNo}`}
              type="file"
              accept=".jpg,.jpeg,.png,.pdf,.mp4"
              className="hidden"
              onChange={(e) => handleFileInput(row.tmNo, e)}
            />
          </div>
        );
      })}

      <FilePreviewModal
        open={!!preview}
        onClose={closePreview}
        name={preview?.name ?? ""}
        url={preview?.url}
        kind={preview?.kind}
      />
    </div>
  );
});

// ---------------------------------------------------------------------------
// FC Fail Documents panel (Substep 1B(vii))
// ---------------------------------------------------------------------------

function DocumentsPanel() {
  return (
    <div
      className="flex flex-col items-center justify-center gap-5 py-6 w-full"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <img
        src={collectDocuments}
        alt="Collect documents"
        className="w-[160px] h-[160px] flex-shrink-0"
      />

      <p
        className="text-sm font-normal text-[#222222] text-center max-w-[360px] leading-6"
      >
        Collect any necessary documents from client for the Reply.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FC Fail Client Confirmation panel (Substep 1B(vi))
// ---------------------------------------------------------------------------

function FcReplyCurrencyAmountField({
  label,
  value,
  onChange,
  readOnly = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  readOnly?: boolean;
}) {
  return (
    <div
      className="flex flex-col items-start gap-1"
      style={{ flex: "1 1 0", minWidth: 0 }}
    >
      <label
        className="flex items-center text-[12px] font-normal leading-5 text-[#222222]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        {label}
      </label>
      <div
        className="flex flex-row justify-between items-center w-full h-9 px-0 gap-2.5"
        style={{
          background: "#FFFFFF",
          border: "1px solid #EEEEEE",
          borderRadius: "6px",
          boxSizing: "border-box",
        }}
      >
        <input
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(e) => {
            if (readOnly) return;
            const cleaned = e.target.value.replace(/[^\d.]/g, "");
            const parts = cleaned.split(".");
            const next =
              parts.length > 1
                ? `${parts[0]}.${parts.slice(1).join("")}`
                : cleaned;
            onChange(next);
          }}
          onKeyDown={(e) => {
            if (readOnly) return;
            if (
              e.key.length === 1 &&
              !e.ctrlKey &&
              !e.metaKey &&
              !/[\d.]/.test(e.key)
            ) {
              e.preventDefault();
            }
          }}
          readOnly={readOnly}
          placeholder="00.00"
          className="flex-1 min-w-0 h-full pl-2.5 pr-0 py-0 bg-transparent text-[12px] font-normal leading-5 text-[#222222] placeholder:text-[#6D6D6D] border-0 outline-none focus:ring-0 disabled:bg-transparent disabled:cursor-default"
          style={{ fontFamily: "Inter, sans-serif" }}
        />
        <div
          className="flex flex-row justify-center items-center shrink-0 px-2.5 py-2 gap-2.5 h-9 min-w-[76px]"
          style={{ background: "#F7F7F7", borderRadius: "0 6px 6px 0" }}
        >
          <span
            className="text-[12px] font-normal leading-5 text-[#6D6D6D]"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            Rs(₹)
          </span>
          <img src={ChevronDownIcon} alt="" className="w-4 h-4 shrink-0" aria-hidden />
        </div>
      </div>
    </div>
  );
}

function formatFcReplyAmount(num: number | null | undefined): string {
  if (num == null || !Number.isFinite(num) || num === 0) return "";
  return num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

type ConfirmationRow = {
  tmNo: string;
  clientGoingAhead: "" | "yes" | "no";
  /** New amount being collected in this submission (input field). */
  collectedAmount: string;
  /** Previously saved cumulative collected amount (shown as "Already collected" hint). */
  savedCollected: number;
  /** This TM's own invoice amount (null when unavailable; falls back to lead total). */
  invoiceAmount: number | null;
};

type ClientConfirmationPanelHandle = { save: () => Promise<void> };

const ClientConfirmationPanel = forwardRef<
  ClientConfirmationPanelHandle,
  { leadId: string | null | undefined; tmNos?: string[]; selectedTmNo?: string }
>(function ClientConfirmationPanel({ leadId, tmNos = [], selectedTmNo }, ref) {
  const [loading, setLoading] = useState(!!leadId);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState<ConfirmationRow[]>([]);
  const [invoiceAmount, setInvoiceAmount] = useState<number | null>(null);
  const [outstandingAmount, setOutstandingAmount] = useState<number | null>(null);

  useEffect(() => {
    if (!leadId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    getFcFailConfirmationFromClient(leadId)
      .then((data: FcFailConfirmationStatus) => {
        if (cancelled) return;
        setInvoiceAmount(data.invoiceAmount);
        setOutstandingAmount(data.outstandingAmount);

        // Always expand to one row per TM so the user gets independent inputs,
        // regardless of whether the backend returns per-TM apps or a single
        // shared record. Seed each row from the matching app when available,
        // otherwise fall back to the top-level shared values from the API.
        const byTmNo = new Map(
          (data.applications ?? []).map((a) => [a.tmNo, a])
        );
        const fallbackGoingAhead =
          data.clientGoingAhead === true
            ? "yes"
            : data.clientGoingAhead === false
            ? "no"
            : "";
        const fallbackSaved = Number(data.collectedAmount ?? 0) || 0;

        if (tmNos.length > 0) {
          setRows(
            tmNos.map((tm): ConfirmationRow => {
              const match = byTmNo.get(tm);
              return {
                tmNo: tm,
                clientGoingAhead:
                  match
                    ? match.clientGoingAhead === true
                      ? "yes"
                      : match.clientGoingAhead === false
                      ? "no"
                      : ""
                    : (fallbackGoingAhead as "" | "yes" | "no"),
                collectedAmount: "",
                savedCollected: match
                  ? Number(match.collectedAmount ?? 0) || 0
                  : fallbackSaved,
                invoiceAmount: match?.invoiceAmount ?? data.invoiceAmount ?? null,
              };
            })
          );
        } else if (data.applications?.length) {
          setRows(
            data.applications.map((a) => ({
              tmNo: a.tmNo,
              clientGoingAhead:
                a.clientGoingAhead === true
                  ? "yes"
                  : a.clientGoingAhead === false
                  ? "no"
                  : "",
              collectedAmount: "",
              savedCollected: Number(a.collectedAmount ?? 0) || 0,
              invoiceAmount: a.invoiceAmount ?? data.invoiceAmount ?? null,
            }))
          );
        } else if (data.clientGoingAhead !== null) {
          setRows([
            {
              tmNo: "",
              clientGoingAhead: fallbackGoingAhead as "" | "yes" | "no",
              collectedAmount: "",
              savedCollected: fallbackSaved,
              invoiceAmount: data.invoiceAmount ?? null,
            },
          ]);
        }
      })
      .catch((err) => {
        if (!cancelled)
          toast.error(err instanceof Error ? err.message : "Failed to load confirmation");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
    // tmNos.join keeps the dep stable across renders with the same TM set
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, tmNos.join("|")]);

  // Recalculate outstanding whenever rows change. Scoped to the displayed TM(s):
  // when the parent has a single TM selected, the bar reflects only that TM's
  // invoice − collected; otherwise it sums each "yes" TM's own per-TM figures.
  useEffect(() => {
    const displayed = rows.filter((r) => !selectedTmNo || r.tmNo === selectedTmNo);
    const yesRows = displayed.filter((r) => r.clientGoingAhead === "yes");
    if (yesRows.length === 0) {
      setOutstandingAmount(null);
      return;
    }
    let total = 0;
    let hasInvoice = false;
    for (const r of yesRows) {
      const rowInvoice = r.invoiceAmount ?? invoiceAmount;
      if (rowInvoice == null) continue;
      hasInvoice = true;
      const n = parseFloat(r.collectedAmount);
      const collected = r.savedCollected + (isNaN(n) ? 0 : n);
      total += rowInvoice - collected;
    }
    setOutstandingAmount(hasInvoice ? total : null);
  }, [rows, invoiceAmount, selectedTmNo]);

  const doSave = async () => {
    if (!leadId) return;

    const applications = rows
      .filter((r) => r.tmNo && r.clientGoingAhead !== "")
      .map((r) => {
        const newAmount = parseFloat(r.collectedAmount);
        const hasNewAmount =
          r.clientGoingAhead === "yes" && r.collectedAmount.trim() !== "" && !isNaN(newAmount);
        return {
          tmNo: r.tmNo,
          clientGoingAhead: r.clientGoingAhead === "yes",
          // Send cumulative total (previously saved + new amount entered now).
          ...(hasNewAmount
            ? { collectedAmount: r.savedCollected + newAmount }
            : r.clientGoingAhead === "yes" && r.savedCollected > 0
            ? { collectedAmount: r.savedCollected }
            : {}),
        };
      });

    if (!applications.length) {
      toast.error("Select a decision for at least one application.");
      throw new Error("No applications to save");
    }

    setSaving(true);
    try {
      await saveFcFailConfirmationFromClient(leadId, { applications });
      const refreshed = await getFcFailConfirmationFromClient(leadId);
      setInvoiceAmount(refreshed.invoiceAmount);
      // Re-seed rows from the persisted per-TM values: roll the just-entered
      // amount into savedCollected, clear the input, and refresh each TM's
      // invoice amount. The outstanding bar then recomputes per-TM via effect.
      const refreshedByTm = new Map(
        (refreshed.applications ?? []).map((a) => [a.tmNo, a])
      );
      setRows((prev) =>
        prev.map((r) => {
          const match = refreshedByTm.get(r.tmNo);
          return {
            ...r,
            collectedAmount: "",
            savedCollected: match
              ? Number(match.collectedAmount ?? 0) || 0
              : r.savedCollected,
            invoiceAmount:
              match?.invoiceAmount ?? r.invoiceAmount ?? refreshed.invoiceAmount ?? null,
          };
        })
      );
      toast.success("Client confirmation saved.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save confirmation");
      throw err;
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({ save: doSave }));

  const updateRow = (idx: number, patch: Partial<ConfirmationRow>) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  if (!leadId) {
    return (
      <p className="text-xs text-[#6D6D6D]" style={{ fontFamily: "Inter, sans-serif" }}>
        Please select a lead.
      </p>
    );
  }

  if (loading) {
    return (
      <p className="text-xs text-[#6D6D6D]" style={{ fontFamily: "Inter, sans-serif" }}>
        Loading…
      </p>
    );
  }

  const labelCls = "block text-xs font-normal text-[#222222] leading-5 mb-1";

  // Determine if we show a single unified row vs per-TM rows
  const isSingleRow = rows.length <= 1;
  const singleRow =
    rows[0] ?? {
      tmNo: "",
      clientGoingAhead: "" as const,
      collectedAmount: "",
      savedCollected: 0,
      invoiceAmount: invoiceAmount,
    };
  const showCollected =
    isSingleRow
      ? singleRow.clientGoingAhead === "yes"
      : rows.some((r) => r.clientGoingAhead === "yes");

  const formatRupees = (n: number) =>
    n.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  return (
    <div
      className="flex flex-col gap-5 w-full"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {isSingleRow ? (
        /* ── Single TM: unified question ── */
        <div className="flex flex-col gap-5">
          <div>
            <label className={labelCls}>Client Agreed to Proceed?</label>
            <Select
              value={singleRow.clientGoingAhead}
              onValueChange={(v) => {
                const val = v as "" | "yes" | "no";
                if (rows.length === 0) {
                  setRows([
                    {
                      tmNo: "",
                      clientGoingAhead: val,
                      collectedAmount: "",
                      savedCollected: 0,
                      invoiceAmount: invoiceAmount,
                    },
                  ]);
                } else {
                  updateRow(0, { clientGoingAhead: val, collectedAmount: "" });
                }
              }}
            >
              <SelectTrigger className="h-9 text-xs border-[#EEEEEE]">
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {singleRow.clientGoingAhead === "yes" && (
            <div className="flex flex-col sm:flex-row items-start sm:items-start gap-5 w-full">
              <div className="flex flex-col gap-1" style={{ flex: "1 1 0", minWidth: 0 }}>
                <FcReplyCurrencyAmountField
                  label="Collected Amount for FC Reply"
                  value={singleRow.collectedAmount}
                  onChange={(next) => updateRow(0, { collectedAmount: next })}
                />
                {singleRow.savedCollected > 0 && (
                  <span className="text-[11px] leading-4 text-[#6D6D6D]">
                    Already collected: Rs. {formatRupees(singleRow.savedCollected)}/-
                  </span>
                )}
              </div>
              <FcReplyCurrencyAmountField
                label="Invoice Amount for FC Reply"
                value={formatFcReplyAmount(singleRow.invoiceAmount ?? invoiceAmount)}
                onChange={() => {}}
                readOnly
              />
            </div>
          )}
        </div>
      ) : (
        /* ── Multi-TM: render only the selected TM's row; the parent's TM pills
              already let the user switch. Full `rows` stays in state so save
              submits every TM and the summary below aggregates across all. ── */
        <div className="flex flex-col gap-3">
          {rows
            .map((row, idx) => ({ row, idx }))
            .filter(({ row }) => !selectedTmNo || row.tmNo === selectedTmNo)
            .map(({ row, idx }) => (
            <div key={row.tmNo || idx} className="flex flex-col gap-3">
              <div>
                <label className={labelCls}>Client Agreed to Proceed?</label>
                <Select
                  value={row.clientGoingAhead}
                  onValueChange={(v) =>
                    updateRow(idx, {
                      clientGoingAhead: v as "" | "yes" | "no",
                      collectedAmount: "",
                    })
                  }
                >
                  <SelectTrigger className="h-9 text-xs border-[#EEEEEE]">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {row.clientGoingAhead === "yes" && (
                <div className="flex flex-col sm:flex-row items-start sm:items-start gap-5 w-full">
                  <div className="flex flex-col gap-1" style={{ flex: "1 1 0", minWidth: 0 }}>
                    <FcReplyCurrencyAmountField
                      label="Collected Amount"
                      value={row.collectedAmount}
                      onChange={(next) => updateRow(idx, { collectedAmount: next })}
                    />
                    {row.savedCollected > 0 && (
                      <span className="text-[11px] leading-4 text-[#6D6D6D]">
                        Already collected: Rs. {formatRupees(row.savedCollected)}/-
                      </span>
                    )}
                  </div>
                  <FcReplyCurrencyAmountField
                    label="Invoice Amount for FC Reply"
                    value={formatFcReplyAmount(row.invoiceAmount ?? invoiceAmount)}
                    onChange={() => {}}
                    readOnly
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Outstanding Amount bar (matches Payment-from-client pattern) */}
      {showCollected && outstandingAmount != null && (
        <div
          className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-3 w-full px-4 sm:px-5 py-4 sm:py-0 min-h-[60px] sm:h-[60px]"
          style={{ background: "#222222", borderRadius: "8px" }}
        >
          <span
            className="text-[12px] sm:text-[14px] font-normal leading-5"
            style={{
              fontFamily: "Inter, sans-serif",
              color: "rgba(255, 255, 255, 0.9)",
            }}
          >
            OUTSTANDING AMOUNT FOR FC REPLY
          </span>
          <span
            className="text-[18px] sm:text-[22px] font-bold leading-8 sm:leading-10 text-white"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            Rs. {Math.max(0, outstandingAmount).toLocaleString("en-IN")}/-
          </span>
        </div>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function FormalitiesCheckView({
  leadId,
  onSuccess,
  onSavingChange,
  onSentStatusChange,
  onFcPassFooterSendModeChange,
  onFcPassFooterSaveModeChange,
  onFcFailNoticeFooterSendModeChange,
  onFcFailInformFooterSendModeChange,
  onFcFailInvoiceFooterSendModeChange,
  onFcFailInformDoneFooterSendModeChange,
  onFcFailStatusFooterFormModeChange,
  onFcFailNoticeFooterSaveModeChange,
  onFcFailInformFooterSaveModeChange,
  onFcFailInvoiceFooterSaveModeChange,
  onFcFailInformDoneFooterSaveModeChange,
  onFcFailCallClientFooterFormModeChange,
  onFcFailConfirmationFooterFormModeChange,
  onFcFailDocumentsFooterFormModeChange,
  onFcFailFilingOfReplyFooterFormModeChange,
  onFcFailInvoiceProgressActionChange,
}: FormalitiesCheckViewProps) {
  // Note: initial fcStatus is reconciled with URL just below where searchParams is read.
  const [fcStatus, setFcStatusState] = useState<FCStatus>("pass");
  const [tmEntries, setTmEntries] = useState<TmEntry[]>([]);
  const [selectedTmIdx, setSelectedTmIdx] = useState(0);
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(!!leadId);
  const [hasSentFcPassInform, setHasSentFcPassInform] = useState(false);
  const [passPreviewMessage, setPassPreviewMessage] = useState<string | null>(null);
  const [passPreviewLoading, setPassPreviewLoading] = useState(false);
  const [resendingFcPass, setResendingFcPass] = useState(false);
  const [fcFailStatus, setFcFailStatus] = useState<FormalitiesCheckFailStatus | null>(null);
  /**
   * Review item 59 — how many applications failed Formalities Check, regardless of
   * which tab is open. `fcFailStatus` is only fetched on the FC Fail tab, so the FC
   * Pass tab had no way to tell the user that FC Failure applications exist. That is
   * the exact case the review calls out ("especially if fc pass scenerio, if it has
   * fc failure applications").
   */
  const [fcFailAppCount, setFcFailAppCount] = useState<number | null>(null);
  const [hasSentFcFailNotice, setHasSentFcFailNotice] = useState(false);
  const [resendingFcFailNotice, setResendingFcFailNotice] = useState(false);
  // Confirm-before-send modal for the FC Fail Notice to Client step (null = closed).
  const [fcFailNoticeConfirm, setFcFailNoticeConfirm] = useState<"send" | "resend" | null>(null);
  // WhatsApp + Email preview shown in that confirm modal.
  const [fcNoticePreviewData, setFcNoticePreviewData] =
    useState<FcFailNoticeToClientPreview | null>(null);
  const [fcNoticePreviewLoading, setFcNoticePreviewLoading] = useState(false);
  const [hasSentFcFailInform, setHasSentFcFailInform] = useState(false);
  const [resendingFcFailInform, setResendingFcFailInform] = useState(false);
  const [hasSentFcFailInvoice, setHasSentFcFailInvoice] = useState(false);
  const [resendingFcFailInvoice, setResendingFcFailInvoice] = useState(false);
  const [fcFailInvoiceSendReady, setFcFailInvoiceSendReady] = useState(false);
  const fcFailInvoicePanelRef = useRef<FcFailInvoicePanelHandle>(null);
  const filingOfReplyPanelRef = useRef<FilingOfReplyPanelHandle>(null);
  const clientConfirmationPanelRef = useRef<ClientConfirmationPanelHandle>(null);
  const [hasSentFcFailInformDone, setHasSentFcFailInformDone] = useState(false);
  const [resendingFcFailInformDone, setResendingFcFailInformDone] = useState(false);
  const [fcFailInformDoneSendReady, setFcFailInformDoneSendReady] = useState(false);
  const [hasRecordedFcFailCallClient, setHasRecordedFcFailCallClient] = useState(false);
  const [progressingFcFailInvoice, setProgressingFcFailInvoice] = useState(false);

  // FC tab + sub-step state, persisted in URL so refresh restores location.
  const [searchParams, setSearchParams] = useSearchParams();
  // eslint-disable-next-line no-console
  console.log("[FormalitiesCheckView] render");
  const validFcFailSubSteps = useMemo(
    () => FC_FAIL_SUB_STEPS.map((s) => s.id) as readonly FCFailSubStep[],
    []
  );
  const urlStatus = searchParams.get("status") as FCStatus | null;
  const urlSubStep = searchParams.get("substep") as FCFailSubStep | null;

  const [fcFailSubStep, setFcFailSubStepState] = useState<FCFailSubStep>(
    urlSubStep && validFcFailSubSteps.includes(urlSubStep)
      ? urlSubStep
      : "status-of-fc-failure"
  );

  const writeFcUrl = (patch: { status?: FCStatus | null; substep?: FCFailSubStep | null }) => {
    const params = new URLSearchParams(searchParams);
    if (patch.status !== undefined) {
      if (patch.status) params.set("status", patch.status);
      else params.delete("status");
    }
    if (patch.substep !== undefined) {
      if (patch.substep) params.set("substep", patch.substep);
      else params.delete("substep");
    }
    setSearchParams(params, { replace: true });
  };

  const setFcFailSubStep = (next: FCFailSubStep) => {
    setFcFailSubStepState(next);
    writeFcUrl({ substep: next });
  };

  const setFcStatus = (next: FCStatus) => {
    setFcStatusState(next);
    writeFcUrl({ status: next, substep: next === "fail" ? undefined : null });
  };

  // Hydrate fcStatus from URL on first render.
  useEffect(() => {
    if ((urlStatus === "pass" || urlStatus === "fail") && urlStatus !== fcStatus) {
      setFcStatusState(urlStatus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "Status of FC Failure" form state — per-TM so editing one TM's date /
  // reason doesn't bleed into the others. Keyed by tmNo.
  const EMPTY_STATUS_FORM: StatusOfFCFailureFormState = {
    dateOfFCNotice: null,
    lastDateAuto: null,
    reasonForFailure: "",
  };
  const [statusOfFCFailureFormByTm, setStatusOfFCFailureFormByTm] = useState<
    Record<string, StatusOfFCFailureFormState>
  >({});

  // FC Notice file upload state (per TM)
  const [fcNoticeFileRows, setFcNoticeFileRows] = useState<FcNoticeFileRow[]>([]);
  const [dragOverFcNotice, setDragOverFcNotice] = useState<string | null>(null);
  const [fcNoticePreview, setFcNoticePreview] = useState<
    { url: string; name: string; kind: FilePreviewKind; revokeOnClose?: boolean } | null
  >(null);
  const [fcNoticeUploadSaving, setFcNoticeUploadSaving] = useState(false);
  // Which TMs to include when sending the Notice to Client (default: all uploaded =
  // send together/combined; backend combines when multiple tmNos are passed).
  const [fcNoticeSelectedTms, setFcNoticeSelectedTms] = useState<string[]>([]);

  // Load lead details + TM entries from acknowledgement
  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      setTmEntries([]);
      setPhone("");
      return;
    }

    let cancelled = false;
    setLoading(true);

    Promise.all([
      getLeadDetails(leadId),
      getFormalitiesCheckStatus(leadId).catch(() => null),
    ])
      .then(([leadRes, fcCheckStatus]) => {
        if (cancelled) return;
        setPhone(leadRes.lead.phone ?? "");
        setTmEntries(
          fcCheckStatus?.applications?.map((a: FormalitiesCheckStatusApplication) => ({
            tmNo: a.tmNo,
            mark: a.mark,
          })) ?? []
        );
        setSelectedTmIdx(0);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load details"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(() => {
    if (!leadId) {
      setHasSentFcPassInform(false);
      setHasSentFcFailNotice(false);
      setHasSentFcFailInform(false);
      setHasSentFcFailInvoice(false);
      setHasSentFcFailInformDone(false);
      setHasRecordedFcFailCallClient(false);
      onSentStatusChange?.(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const status = await getWorkflowStatus(leadId);
        const findStep = (slug: string) =>
          status.steps.find((s) => s.slug?.toUpperCase() === slug.toUpperCase());
        const passStep = findStep("S3_01A_FORMALITIES_CHECK_PASS_INFORM_CLIENT");
        const noticeStep = findStep("S3_01B_II_NOTICE_TO_CLIENT_FC_FAIL");
        const informStep = findStep("S3_01B_III_INFORM_CLIENT_FC_FAIL");
        const invoiceSendStep = findStep("S3_01B_IV_C_SEND_INVOICE_TO_CLIENT_FC_FAIL");
        const informDoneStep = findStep("S3_01B_IX_INFORM_CLIENT_REPLY_TO_FC_FAIL");
        const callClientStep = findStep("S3_01B_V_CALL_CLIENT_FC_FAIL");
        if (!cancelled) {
          const passDone = Boolean(passStep?.isCompleted);
          setHasSentFcPassInform(passDone);
          onSentStatusChange?.(passDone);
          setHasSentFcFailNotice(Boolean(noticeStep?.isCompleted));
          setHasSentFcFailInform(Boolean(informStep?.isCompleted));
          setHasSentFcFailInvoice(Boolean(invoiceSendStep?.isCompleted));
          setHasSentFcFailInformDone(Boolean(informDoneStep?.isCompleted));
          setHasRecordedFcFailCallClient(Boolean(callClientStep?.isCompleted));
        }
      } catch {
        if (!cancelled) {
          setHasSentFcPassInform(false);
          setHasSentFcFailNotice(false);
          setHasSentFcFailInform(false);
          setHasSentFcFailInvoice(false);
          setHasSentFcFailInformDone(false);
          setHasRecordedFcFailCallClient(false);
          onSentStatusChange?.(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, onSentStatusChange]);

  useEffect(() => {
    if (!leadId || fcStatus !== "fail") {
      setFcFailStatus(null);
      return;
    }
    let cancelled = false;
    getFormalitiesCheckFailStatus(leadId)
      .then((data) => {
        if (!cancelled) setFcFailStatus(data);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load FC Fail status"
          );
          setFcFailStatus(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, fcStatus, fcFailSubStep]);

  // Item 59: FC Fail count for the cross-branch notice — loaded once per lead so it
  // is available on the FC Pass tab too.
  useEffect(() => {
    if (!leadId) {
      setFcFailAppCount(null);
      return;
    }
    let cancelled = false;
    getFormalitiesCheckFailStatus(leadId)
      .then((data) => {
        if (!cancelled) setFcFailAppCount(data?.applications?.length ?? 0);
      })
      .catch(() => {
        // Non-fatal: the notice simply does not render.
        if (!cancelled) setFcFailAppCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(() => {
    if (!fcFailStatus) {
      setFcNoticeFileRows([]);
      return;
    }
    setFcNoticeFileRows((prev) =>
      fcFailStatus.applications.map((a) => {
        const existing = prev.find((r) => r.tmNo === a.tmNo);
        return {
          tmNo: a.tmNo,
          fileKey: a.noticeOfFcFailFileKey,
          filename: a.noticeOfFcFailFilename,
          fileUrl: a.noticeOfFcFailFileUrl,
          pendingFile: existing?.pendingFile ?? null,
        };
      })
    );
    // Hydrate per-TM form state — keep any in-flight user edits, fall back to backend.
    setStatusOfFCFailureFormByTm((prev) => {
      const next: Record<string, StatusOfFCFailureFormState> = {};
      for (const a of fcFailStatus.applications) {
        const existing = prev[a.tmNo];
        next[a.tmNo] = existing ?? {
          dateOfFCNotice: parseFcInvoiceDate(a.dateOfFcNotice),
          lastDateAuto: parseFcInvoiceDate(a.lastDateForFcReply),
          reasonForFailure: a.reasonForFcFailure ?? "",
        };
      }
      return next;
    });
  }, [fcFailStatus]);

  // Default the Notice send-selection to all uploaded notices; preserve the user's
  // existing ticks, and never leave it empty when notices are available.
  useEffect(() => {
    const available = fcNoticeFileRows
      .filter((r) => r.fileUrl)
      .map((r) => r.tmNo);
    setFcNoticeSelectedTms((prev) => {
      const validPrev = prev.filter((t) => available.includes(t));
      return validPrev.length ? validPrev : available;
    });
  }, [fcNoticeFileRows]);

  // Load the WhatsApp + Email preview whenever the confirm-send modal opens.
  useEffect(() => {
    if (!leadId || fcFailNoticeConfirm === null || fcNoticeSelectedTms.length === 0) {
      return;
    }
    let cancelled = false;
    setFcNoticePreviewLoading(true);
    setFcNoticePreviewData(null);
    getFcFailNoticeToClientPreview(leadId, fcNoticeSelectedTms)
      .then((data) => {
        if (!cancelled) setFcNoticePreviewData(data);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load notice preview"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setFcNoticePreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, fcFailNoticeConfirm, fcNoticeSelectedTms]);

  useEffect(() => {
    if (fcFailSubStep !== "invoice-for-fc-failure") {
      setFcFailInvoiceSendReady(false);
    }
    if (fcFailSubStep !== "inform-client-done") {
      setFcFailInformDoneSendReady(false);
    }
  }, [fcFailSubStep]);

  useEffect(() => {
    if (!leadId || fcStatus !== "pass") {
      setPassPreviewMessage(null);
      setPassPreviewLoading(false);
      return;
    }
    const tmNo = tmEntries[selectedTmIdx]?.tmNo;
    if (!tmNo) {
      setPassPreviewMessage(null);
      setPassPreviewLoading(false);
      return;
    }
    let cancelled = false;
    setPassPreviewLoading(true);
    getFcPassInformClientPreview(leadId, [tmNo])
      .then((res) => {
        if (!cancelled) setPassPreviewMessage(res.message);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load message preview"
          );
          setPassPreviewMessage(null);
        }
      })
      .finally(() => {
        if (!cancelled) setPassPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, fcStatus, selectedTmIdx, tmEntries]);

  useLayoutEffect(() => {
    const needsSend =
      fcStatus === "pass" &&
      !hasSentFcPassInform &&
      tmEntries.length > 0 &&
      Boolean(tmEntries[selectedTmIdx]?.tmNo);
    onFcPassFooterSendModeChange?.(needsSend);
  }, [
    fcStatus,
    hasSentFcPassInform,
    tmEntries,
    selectedTmIdx,
    onFcPassFooterSendModeChange,
  ]);

  // After the FC Pass update is sent, the footer shows "Save" (no "Continue").
  // The form submit handler no-ops once sent, so "Save" keeps the user on the
  // step; resending is handled by the inline "Resend to WhatsApp" button.
  useLayoutEffect(() => {
    const needsSave = fcStatus === "pass" && hasSentFcPassInform;
    onFcPassFooterSaveModeChange?.(needsSave);
  }, [fcStatus, hasSentFcPassInform, onFcPassFooterSaveModeChange]);

  const canSendFcFailNotice = Boolean(
    fcFailStatus?.applications?.some((a) => a.noticeOfFcFailFileKey)
  );

  useLayoutEffect(() => {
    // canSend* gate intentionally dropped — the form must always be wired so
    // Save & Continue can advance even when the message can't be sent yet.
    // The submit handler advances if can't send.
    const needsSend =
      fcStatus === "fail" &&
      fcFailSubStep === "notice-to-client" &&
      !hasSentFcFailNotice;
    onFcFailNoticeFooterSendModeChange?.(needsSend);
  }, [
    fcStatus,
    fcFailSubStep,
    hasSentFcFailNotice,
    onFcFailNoticeFooterSendModeChange,
  ]);

  const canSendFcFailInform = (fcFailStatus?.applications?.length ?? 0) > 0;

  useLayoutEffect(() => {
    const needsSend =
      fcStatus === "fail" &&
      fcFailSubStep === "inform-client" &&
      !hasSentFcFailInform;
    onFcFailInformFooterSendModeChange?.(needsSend);
  }, [
    fcStatus,
    fcFailSubStep,
    hasSentFcFailInform,
    onFcFailInformFooterSendModeChange,
  ]);

  useLayoutEffect(() => {
    const needsSend =
      fcStatus === "fail" &&
      fcFailSubStep === "invoice-for-fc-failure" &&
      !hasSentFcFailInvoice;
    onFcFailInvoiceFooterSendModeChange?.(needsSend);
  }, [
    fcStatus,
    fcFailSubStep,
    hasSentFcFailInvoice,
    onFcFailInvoiceFooterSendModeChange,
  ]);

  useLayoutEffect(() => {
    const needsSend =
      fcStatus === "fail" &&
      fcFailSubStep === "inform-client-done" &&
      !hasSentFcFailInformDone;
    onFcFailInformDoneFooterSendModeChange?.(needsSend);
  }, [
    fcStatus,
    fcFailSubStep,
    hasSentFcFailInformDone,
    onFcFailInformDoneFooterSendModeChange,
  ]);

  // FC Fail Status of FC Failure: footer "Save & Continue" submits the form (advances sub-step)
  useLayoutEffect(() => {
    const needsSubmit =
      fcStatus === "fail" && fcFailSubStep === "status-of-fc-failure";
    onFcFailStatusFooterFormModeChange?.(needsSubmit);
  }, [fcStatus, fcFailSubStep, onFcFailStatusFooterFormModeChange]);

  // Save-mode signals for the four send-style FC Fail panels: after sent,
  // footer "Save & Continue" submits the same form to advance the sub-step.
  useLayoutEffect(() => {
    const needsSave =
      fcStatus === "fail" &&
      fcFailSubStep === "notice-to-client" &&
      hasSentFcFailNotice;
    onFcFailNoticeFooterSaveModeChange?.(needsSave);
  }, [
    fcStatus,
    fcFailSubStep,
    hasSentFcFailNotice,
    onFcFailNoticeFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    const needsSave =
      fcStatus === "fail" &&
      fcFailSubStep === "inform-client" &&
      hasSentFcFailInform;
    onFcFailInformFooterSaveModeChange?.(needsSave);
  }, [
    fcStatus,
    fcFailSubStep,
    hasSentFcFailInform,
    onFcFailInformFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    const needsSave =
      fcStatus === "fail" &&
      fcFailSubStep === "invoice-for-fc-failure" &&
      hasSentFcFailInvoice;
    onFcFailInvoiceFooterSaveModeChange?.(needsSave);
  }, [
    fcStatus,
    fcFailSubStep,
    hasSentFcFailInvoice,
    onFcFailInvoiceFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    const needsSave =
      fcStatus === "fail" &&
      fcFailSubStep === "inform-client-done" &&
      hasSentFcFailInformDone;
    onFcFailInformDoneFooterSaveModeChange?.(needsSave);
  }, [
    fcStatus,
    fcFailSubStep,
    hasSentFcFailInformDone,
    onFcFailInformDoneFooterSaveModeChange,
  ]);

  // Form-mode signals for the four panels without forms: footer "Save & Continue"
  // submits a passthrough form that advances the sub-step.
  useLayoutEffect(() => {
    const needsSubmit =
      fcStatus === "fail" && fcFailSubStep === "call-client";
    onFcFailCallClientFooterFormModeChange?.(needsSubmit);
  }, [fcStatus, fcFailSubStep, onFcFailCallClientFooterFormModeChange]);

  useLayoutEffect(() => {
    const needsSubmit =
      fcStatus === "fail" && fcFailSubStep === "client-confirmation";
    onFcFailConfirmationFooterFormModeChange?.(needsSubmit);
  }, [fcStatus, fcFailSubStep, onFcFailConfirmationFooterFormModeChange]);

  useLayoutEffect(() => {
    const needsSubmit = fcStatus === "fail" && fcFailSubStep === "documents";
    onFcFailDocumentsFooterFormModeChange?.(needsSubmit);
  }, [fcStatus, fcFailSubStep, onFcFailDocumentsFooterFormModeChange]);

  useLayoutEffect(() => {
    const needsSubmit =
      fcStatus === "fail" && fcFailSubStep === "filing-of-reply";
    onFcFailFilingOfReplyFooterFormModeChange?.(needsSubmit);
  }, [fcStatus, fcFailSubStep, onFcFailFilingOfReplyFooterFormModeChange]);

  // Register the Progress (skip) handler with the dashboard footer when this
  // sub-step is active, so the footer can render its Progress button between
  // Close and Save & Continue, matching the rest of the app.
  useEffect(() => {
    if (!onFcFailInvoiceProgressActionChange) return;
    const active =
      fcStatus === "fail" && fcFailSubStep === "invoice-for-fc-failure";
    onFcFailInvoiceProgressActionChange(active ? handleFcFailInvoiceProgress : null);
    return () => onFcFailInvoiceProgressActionChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fcStatus, fcFailSubStep, onFcFailInvoiceProgressActionChange]);

  // Reset sub-step when switching to FC Fail; keep both state + URL in sync via one write.
  const handleFcStatusChange = (status: FCStatus) => {
    setFcStatusState(status);
    setSelectedTmIdx(0);
    if (status === "fail") {
      setFcFailSubStepState("status-of-fc-failure");
      writeFcUrl({ status, substep: "status-of-fc-failure" });
    } else {
      writeFcUrl({ status, substep: null });
    }
  };

  // On FC Fail tab, only show applications whose status is "Formalities Check Fail"
  // (sourced from /formalities-check-fail). On FC Pass tab, keep the full list
  // returned by /formalities-check.
  const displayTmEntries: TmEntry[] = useMemo(() => {
    if (fcStatus === "fail") {
      return (fcFailStatus?.applications ?? []).map((a) => ({
        tmNo: a.tmNo,
        mark: a.mark,
      }));
    }
    return tmEntries;
  }, [fcStatus, fcFailStatus, tmEntries]);

  const selectedEntry = displayTmEntries[selectedTmIdx] ?? { tmNo: "", mark: "" };

  const handleFcPassFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (hasSentFcPassInform || !leadId) return;
    const tmNo = selectedEntry.tmNo;
    if (!tmNo) {
      toast.error("Select a TM application to include in the message.");
      return;
    }
    onSavingChange?.(true);
    try {
      await sendFcPassInformClientWhatsApp(leadId, [tmNo]);
      setHasSentFcPassInform(true);
      onSentStatusChange?.(true);
      toast.success("Formalities Check Pass update sent to client.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send WhatsApp");
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleResendFcPassWhatsApp = async () => {
    if (!leadId || resendingFcPass) return;
    const tmNo = selectedEntry.tmNo;
    if (!tmNo) return;
    setResendingFcPass(true);
    onSavingChange?.(true);
    try {
      await sendFcPassInformClientWhatsApp(leadId, [tmNo]);
      toast.success("Formalities Check Pass update resent to client.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resend");
    } finally {
      setResendingFcPass(false);
      onSavingChange?.(false);
    }
  };

  const handleFcFailStatusFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || !fcNoticeFileRows.length) {
      setFcFailSubStep("notice-to-client");
      return;
    }

    // All fields are required for every trademark application before continuing.
    const multipleTms = fcNoticeFileRows.length > 1;
    for (const row of fcNoticeFileRows) {
      const tmForm = statusOfFCFailureFormByTm[row.tmNo] ?? EMPTY_STATUS_FORM;
      const missingField = !tmForm.dateOfFCNotice
        ? "Date of FC Notice"
        : !tmForm.lastDateAuto
        ? "Last Date for FC Reply"
        : !tmForm.reasonForFailure.trim()
        ? "Reason for Failure"
        : !(row.pendingFile || row.fileKey)
        ? "Notice of FC Fail PDF"
        : null;
      if (missingField) {
        const idx = displayTmEntries.findIndex((e) => e.tmNo === row.tmNo);
        if (idx >= 0) setSelectedTmIdx(idx);
        toast.error(
          multipleTms
            ? `${missingField} is required for ${row.tmNo}.`
            : `${missingField} is required.`
        );
        return;
      }
    }

    setFcNoticeUploadSaving(true);
    onSavingChange?.(true);
    try {
      // Upload any pending files first using generic presigned URL endpoint
      const pendingRows = fcNoticeFileRows.filter((r) => r.pendingFile);
      const uploadedKeys: Record<string, string> = {};
      if (pendingRows.length > 0) {
        const presignedUrls = await generateAcknowledgementUploadUrls(
          pendingRows.map((r) => ({
            filename: r.pendingFile!.name,
            mimeType: r.pendingFile!.type || "application/pdf",
          })),
          "fc-fail-notices"
        );
        for (let i = 0; i < pendingRows.length; i++) {
          const row = pendingRows[i];
          const item = presignedUrls[i];
          if (!row.pendingFile || !item?.url) continue;
          const res = await fetch(item.url, {
            method: "PUT",
            body: row.pendingFile,
            headers: { "Content-Type": row.pendingFile.type || "application/pdf" },
          });
          if (!res.ok) throw new Error(`Failed to upload notice PDF for ${row.tmNo}`);
          uploadedKeys[row.tmNo] = item.key;
        }
      }

      const updated = await setFormalitiesCheckFailData(leadId, {
        applications: fcNoticeFileRows.map((r) => {
          const tmForm = statusOfFCFailureFormByTm[r.tmNo] ?? EMPTY_STATUS_FORM;
          const tmDate = toIsoDate(tmForm.dateOfFCNotice);
          const tmLastDate = toIsoDate(tmForm.lastDateAuto);
          const tmReason = tmForm.reasonForFailure.trim() || undefined;
          return {
            tmNo: r.tmNo,
            ...(tmDate ? { dateOfFcNotice: tmDate } : {}),
            ...(tmLastDate ? { lastDateForFcReply: tmLastDate } : {}),
            ...(tmReason ? { reasonForFcFailure: tmReason } : {}),
            ...(uploadedKeys[r.tmNo]
              ? {
                  noticeOfFcFailFileKey: uploadedKeys[r.tmNo],
                  noticeOfFcFailFilename: r.pendingFile?.name ?? r.filename ?? "",
                }
              : r.fileKey
              ? { noticeOfFcFailFileKey: r.fileKey, noticeOfFcFailFilename: r.filename ?? "" }
              : {}),
          };
        }),
      });

      setFcFailStatus(updated);
      toast.success("FC Fail status saved.");
      setFcFailSubStep("notice-to-client");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save FC Fail status");
    } finally {
      setFcNoticeUploadSaving(false);
      onSavingChange?.(false);
    }
  };

  const handleFcFailCallClientFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    onSavingChange?.(true);
    try {
      await recordCallClientFcFail(leadId);
      setFcFailSubStep("client-confirmation");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to record Call Client step"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleFcFailConfirmationFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    onSavingChange?.(true);
    try {
      await clientConfirmationPanelRef.current?.save();
      setFcFailSubStep("documents");
    } catch {
      // error already toasted inside save()
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleFcFailDocumentsFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    onSavingChange?.(true);
    try {
      await recordDocumentsFromClientFcFail(leadId);
      setFcFailSubStep("filing-of-reply");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to record step");
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleFcFailFilingOfReplyFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    onSavingChange?.(true);
    try {
      await filingOfReplyPanelRef.current?.save();
      setFcFailSubStep("inform-client-done");
    } catch {
      // error already toasted inside save()
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleFcFailNoticeFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    // Already sent (or can't send right now): just advance the sub-step.
    if (hasSentFcFailNotice || !canSendFcFailNotice) {
      setFcFailSubStep("inform-client");
      return;
    }
    if (fcNoticeSelectedTms.length === 0) {
      toast.error("Select at least one application to send the notice.");
      return;
    }
    // Confirm the notice preview before sending.
    setFcFailNoticeConfirm("send");
  };

  const handleResendFcFailNoticeWhatsApp = () => {
    if (!leadId || resendingFcFailNotice || !canSendFcFailNotice) return;
    if (fcNoticeSelectedTms.length === 0) {
      toast.error("Select at least one application to resend the notice.");
      return;
    }
    setFcFailNoticeConfirm("resend");
  };

  const confirmSendFcFailNotice = async () => {
    const mode = fcFailNoticeConfirm;
    setFcFailNoticeConfirm(null);
    if (!leadId || mode === null) return;
    if (fcNoticeSelectedTms.length === 0) return;
    if (mode === "resend") setResendingFcFailNotice(true);
    onSavingChange?.(true);
    try {
      await sendFcFailNoticeToClientWhatsApp(leadId, fcNoticeSelectedTms);
      if (mode === "send") {
        setHasSentFcFailNotice(true);
        toast.success("Notice of Formality Check Fail sent to client.");
        setFcFailSubStep("inform-client");
      } else {
        toast.success("Notice of Formality Check Fail resent to client.");
      }
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : mode === "resend"
            ? "Failed to resend notice"
            : "Failed to send notice to WhatsApp"
      );
    } finally {
      if (mode === "resend") setResendingFcFailNotice(false);
      onSavingChange?.(false);
    }
  };

  const handleFcFailInformFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSentFcFailInform || !canSendFcFailInform) {
      setFcFailSubStep("invoice-for-fc-failure");
      return;
    }
    onSavingChange?.(true);
    try {
      await sendFcFailInformClientWhatsApp(leadId);
      setHasSentFcFailInform(true);
      toast.success("Inform Client message sent via WhatsApp.");
      setFcFailSubStep("invoice-for-fc-failure");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to send Inform Client message"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleResendFcFailInformWhatsApp = async () => {
    if (!leadId || resendingFcFailInform || !canSendFcFailInform) return;
    setResendingFcFailInform(true);
    onSavingChange?.(true);
    try {
      await sendFcFailInformClientWhatsApp(leadId);
      toast.success("Inform Client message resent via WhatsApp.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resend message");
    } finally {
      setResendingFcFailInform(false);
      onSavingChange?.(false);
    }
  };

  const handleFcFailInvoiceFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    onSavingChange?.(true);
    try {
      // Always persist the current form values — even if the invoice was sent
      // previously, the user may have edited the amount/date and expects those
      // edits to save when they hit Save & Continue.
      await fcFailInvoicePanelRef.current?.save();

      if (hasSentFcFailInvoice) {
        // Already sent — don't re-send; just advance.
        setFcFailSubStep("call-client");
        return;
      }
      if (!fcFailInvoiceSendReady) {
        setFcFailSubStep("call-client");
        return;
      }
      const sendResult = await sendFcReplyInvoiceToClient(leadId, {
        sendWhatsApp: true,
        sendEmail: true,
      });
      setHasSentFcFailInvoice(true);
      toast.success(
        sendResult.emailSent
          ? "Invoice sent to client via WhatsApp and email."
          : "Invoice sent to client via WhatsApp."
      );
      setFcFailSubStep("call-client");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save invoice"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleFcFailInvoiceProgress = async () => {
    if (!leadId || progressingFcFailInvoice) return;
    setProgressingFcFailInvoice(true);
    try {
      await skipStep(leadId, "S3_01B_IV_C_SEND_INVOICE_TO_CLIENT_FC_FAIL");
      toast.success("Step skipped successfully.");
      setFcFailSubStep("call-client");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to skip step");
    } finally {
      setProgressingFcFailInvoice(false);
    }
  };

  const handleResendFcFailInvoiceWhatsApp = async () => {
    if (!leadId || resendingFcFailInvoice || !fcFailInvoiceSendReady) return;
    setResendingFcFailInvoice(true);
    onSavingChange?.(true);
    try {
      const { waLink, emailSent } = await sendFcReplyInvoiceToClient(leadId, {
        sendWhatsApp: true,
        sendEmail: true,
      });
      if (waLink) window.open(waLink, "_blank", "noopener,noreferrer");
      toast.success(
        emailSent
          ? "Invoice resent to client via WhatsApp and email."
          : "Invoice resent to client via WhatsApp."
      );
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to resend invoice to client"
      );
    } finally {
      setResendingFcFailInvoice(false);
      onSavingChange?.(false);
    }
  };

  const handleFcFailInformDoneFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSentFcFailInformDone || !fcFailInformDoneSendReady) {
      return;
    }
    onSavingChange?.(true);
    try {
      await sendFcReplyFiledInformClientWhatsApp(leadId);
      setHasSentFcFailInformDone(true);
      toast.success("Inform Client (Done) sent via WhatsApp.");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to send Inform Client (Done) to WhatsApp"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleResendFcFailInformDoneWhatsApp = async () => {
    if (!leadId || resendingFcFailInformDone || !fcFailInformDoneSendReady) return;
    setResendingFcFailInformDone(true);
    onSavingChange?.(true);
    try {
      await sendFcReplyFiledInformClientWhatsApp(leadId);
      toast.success("Inform Client (Done) resent via WhatsApp.");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to resend Inform Client (Done) to WhatsApp"
      );
    } finally {
      setResendingFcFailInformDone(false);
      onSavingChange?.(false);
    }
  };

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  const fcOptions: { id: FCStatus; label: string }[] = [
    { id: "pass", label: "FC Pass" },
    { id: "fail", label: "FC Fail" },
  ];

  return (
    <div
      className="flex flex-col gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* ── FC Pass / FC Fail toggle ── */}
      <div className="flex flex-row flex-wrap gap-3">
        {fcOptions.map((opt) => {
          const isActive = fcStatus === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => handleFcStatusChange(opt.id)}
              className={`flex items-center gap-2.5 px-2.5 py-2.5 rounded-[6px] h-10 min-w-[160px] text-xs font-normal transition-colors ${
                isActive
                  ? "bg-[#222222] text-white"
                  : "bg-[#F7F7F7] text-[#222222]"
              }`}
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              <RadioCircle active={isActive} />
              <span className="text-left line-clamp-1">{opt.label}</span>
            </button>
          );
        })}
      </div>

      {/* ── TM Number selector ──
           Always rendered when entries are available.
           Populated from getAcknowledgementSummary once a leadId is present.
      */}
      {displayTmEntries.length > 0 && (
        <div className="flex flex-row flex-wrap gap-3">
          {displayTmEntries.map((entry, idx) => {
            const isActive = selectedTmIdx === idx;
            return (
              <button
                key={entry.tmNo}
                type="button"
                onClick={() => setSelectedTmIdx(idx)}
                className={`flex items-center gap-2 px-2.5 py-2.5 rounded-[6px] h-10 min-w-[140px] transition-colors ${
                  isActive
                    ? "bg-[#222222] text-white"
                    : "bg-[#F7F7F7] text-[#222222]"
                }`}
                style={{ fontFamily: "Inter, sans-serif", fontSize: "0.75rem" }}
              >
                <RadioCircle active={isActive} />
                {formatTmNoForDisplay(entry.tmNo)}
              </button>
            );
          })}
        </div>
      )}

      {/* Item 59: position in the set + a way to reach the next application, and a
          pointer to the other branch when it holds applications (the FC Pass tab
          otherwise gives no clue that FC Failure applications are waiting). */}
      <MultiApplicationNav
        total={displayTmEntries.length}
        currentIdx={selectedTmIdx}
        onSelectIdx={(idx) => {
          if (idx < 0 || idx >= displayTmEntries.length) return;
          setSelectedTmIdx(idx);
        }}
        otherBranchCount={
          fcStatus === "pass" ? (fcFailAppCount ?? 0) : 0
        }
        otherBranchLabel="Formalities Check Failure"
        onSwitchBranch={() => handleFcStatusChange("fail")}
      />

      {/* ── Content: FC Pass → Message Preview  |  FC Fail → Sub-steps + Form ── */}
      {displayTmEntries.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center gap-1 border border-dashed border-[#EEEEEE] rounded-xl py-12 px-6 text-center"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <p className="text-sm font-medium text-[#222222]">
            No applications {fcStatus === "pass" ? "passed Formalities Check" : "failed Formalities Check"} yet.
          </p>
          <p className="text-xs text-[#6D6D6D]">
            {fcStatus === "pass"
              ? "Applications will appear here once their Formalities Check status is set to Pass."
              : "Applications will appear here once their Formalities Check status is set to Fail."}
          </p>
        </div>
      ) : fcStatus === "pass" ? (
        /* ── FC Pass: Message Preview (send via dashboard footer: Send & Continue) ── */
        <form
          id="formalities-check-fc-pass-form"
          onSubmit={handleFcPassFormSubmit}
          className="flex flex-col gap-2.5"
        >
          <span
            className="text-sm font-normal text-[#222222]"
            style={{ lineHeight: "20px" }}
          >
            Message Preview
          </span>
          <div className="border border-[#EEEEEE] rounded-xl overflow-hidden">
            <div className="px-4 py-4 min-h-[80px]">
              {passPreviewLoading ? (
                <p className="text-xs text-[#6D6D6D]" style={{ lineHeight: "20px" }}>
                  Loading preview…
                </p>
              ) : (
                <p
                  className="text-xs text-[#222222] whitespace-pre-line"
                  style={{ lineHeight: "20px" }}
                >
                  {passPreviewMessage ? renderWhatsAppFormatting(passPreviewMessage) : "—"}
                </p>
              )}
            </div>
            <div className="border-t border-[#EEEEEE] flex items-center gap-3 px-3 py-2.5">
              <div className="flex-1 min-w-0 h-9 flex items-center px-2.5 rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7]">
                <span
                  className="text-xs text-[#222222] truncate"
                  style={{ lineHeight: "20px" }}
                >
                  {phone || "(No phone number)"}
                </span>
              </div>
            </div>
          </div>
          {hasSentFcPassInform && leadId && (
            <div className="flex flex-row items-center gap-3 w-full">
              <button
                type="button"
                disabled={resendingFcPass || passPreviewLoading}
                onClick={() => void handleResendFcPassWhatsApp()}
                className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {resendingFcPass ? "Resending…" : "Resend to WhatsApp"}
              </button>
            </div>
          )}
        </form>
      ) : (
        /* ── FC Fail: two-column panel ── */
        <div className="border border-[#EEEEEE] rounded-xl overflow-hidden flex min-h-0">
          {/* Left: sub-step list */}
          <div className="w-[185px] flex-shrink-0 border-r border-[#EEEEEE] p-2 flex flex-col gap-1 overflow-y-auto">
            {FC_FAIL_SUB_STEPS.map((step) => {
              const isActive = fcFailSubStep === step.id;
              return (
                <button
                  key={step.id}
                  type="button"
                  onClick={() => setFcFailSubStep(step.id)}
                  className={`w-full flex items-center justify-between px-2.5 py-2.5 gap-2.5 rounded-[6px] text-xs font-${isActive ? "500" : "400"} transition-colors ${
                    isActive
                      ? "bg-[#222222] text-white"
                      : "bg-[#F7F7F7] text-[#222222] hover:bg-[#EEEEEE]"
                  }`}
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  <span className="text-left line-clamp-1" title={step.label}>{step.label}</span>
                  <ChevronRight active={isActive} />
                </button>
              );
            })}
          </div>

          {/* Right: detail form / panel */}
          <div className="flex-1 min-w-0 px-4 py-4 overflow-y-auto">
            {fcFailSubStep === "status-of-fc-failure" ? (
              <form
                id="formalities-check-fc-fail-status-form"
                onSubmit={(ev) => void handleFcFailStatusFormSubmit(ev)}
                className="flex flex-col w-full gap-5"
              >
                <StatusOfFCFailureForm
                  value={
                    statusOfFCFailureFormByTm[selectedEntry.tmNo] ?? EMPTY_STATUS_FORM
                  }
                  onChange={(v) =>
                    setStatusOfFCFailureFormByTm((prev) => ({
                      ...prev,
                      [selectedEntry.tmNo]: v,
                    }))
                  }
                />

                {/* FC Notice PDF upload — scoped to the selected TM so each
                    application gets its own file (uploading for one TM never
                    shows under another). */}
                {fcNoticeFileRows.some((r) => r.tmNo === selectedEntry.tmNo) && (
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-0.5">
                      <p className="text-sm font-medium text-[#222222]" style={{ fontFamily: "Inter, sans-serif" }}>
                        Notice of FC Fail PDF{REQ}
                      </p>
                      <p className="text-xs text-[#6D6D6D]" style={{ fontFamily: "Inter, sans-serif" }}>
                        Upload the notice PDF for this trademark application.
                      </p>
                    </div>

                    {fcNoticeFileRows
                      .filter((row) => row.tmNo === selectedEntry.tmNo)
                      .map((row) => {
                      const isDragging = dragOverFcNotice === row.tmNo;
                      const hasFile = !!(row.pendingFile || row.fileKey);
                      return (
                        <div key={row.tmNo} className="flex flex-col gap-2">
                          {displayTmEntries.length > 1 && (
                            <p className="text-xs font-semibold text-[#222222]" style={{ fontFamily: "Inter, sans-serif" }}>{row.tmNo}</p>
                          )}
                          <div
                            className={`flex items-center gap-4 rounded-lg border border-dashed px-4 py-4 cursor-pointer transition-colors ${
                              isDragging
                                ? "border-[#222222] bg-[#F5F5F5]"
                                : hasFile
                                ? "border-[#22C55E] bg-[#F0FDF4]"
                                : "border-[#D0D0D0] bg-white"
                            }`}
                            onDragOver={(e) => { e.preventDefault(); setDragOverFcNotice(row.tmNo); }}
                            onDragLeave={() => setDragOverFcNotice(null)}
                            onDrop={(e) => {
                              e.preventDefault();
                              setDragOverFcNotice(null);
                              const file = e.dataTransfer.files[0];
                              if (file) setFcNoticeFileRows((prev) => prev.map((r) => r.tmNo === row.tmNo ? { ...r, pendingFile: file, filename: file.name } : r));
                            }}
                            onClick={() => document.getElementById(`fc-notice-file-input-${row.tmNo}`)?.click()}
                          >
                            <div className="flex-shrink-0">
                              <svg width="36" height="40" viewBox="0 0 36 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M22 1H5C3.9 1 3 1.9 3 3V37C3 38.1 3.9 39 5 39H31C32.1 39 33 38.1 33 37V12L22 1Z" fill="#E2E8F0" stroke="#CBD5E0" strokeWidth="1.5" />
                                <path d="M22 1V12H33" fill="#CBD5E0" stroke="#CBD5E0" strokeWidth="1.5" />
                                <line x1="10" y1="20" x2="26" y2="20" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" />
                                <line x1="10" y1="25" x2="26" y2="25" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" />
                                <line x1="10" y1="30" x2="20" y2="30" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              {hasFile ? (
                                <p className="text-sm font-medium text-[#16A34A] truncate" style={{ fontFamily: "Inter, sans-serif" }}>
                                  {row.pendingFile?.name ?? row.filename ?? "File selected"}
                                </p>
                              ) : (
                                <>
                                  <p className="text-sm font-medium text-[#222222]" style={{ fontFamily: "Inter, sans-serif" }}>Drag and drop your file</p>
                                  <p className="text-xs text-[#6D6D6D] mt-0.5" style={{ fontFamily: "Inter, sans-serif" }}>PDF format, up to 50MB</p>
                                </>
                              )}
                            </div>
                            {!hasFile && (
                              <button
                                type="button"
                                className="flex-shrink-0 px-4 py-2 rounded-lg border border-[#D0D0D0] bg-white text-sm font-medium text-[#222222] hover:bg-[#F5F5F5] transition-colors"
                                onClick={(e) => { e.stopPropagation(); document.getElementById(`fc-notice-file-input-${row.tmNo}`)?.click(); }}
                              >
                                Select File
                              </button>
                            )}
                            {hasFile && (
                              <>
                                <button
                                  type="button"
                                  className="flex-shrink-0 px-3 py-1.5 rounded-lg border border-[#D0D0D0] bg-white text-xs font-medium text-[#2E3037] hover:bg-[#F5F5F5]"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (row.pendingFile) {
                                      setFcNoticePreview({ url: URL.createObjectURL(row.pendingFile), name: row.pendingFile.name, kind: detectPreviewKind(row.pendingFile.name, row.pendingFile.type), revokeOnClose: true });
                                    } else if (row.fileUrl) {
                                      setFcNoticePreview({ url: row.fileUrl, name: row.filename ?? "Notice PDF", kind: detectPreviewKind(row.filename ?? "") });
                                    }
                                  }}
                                >
                                  Preview
                                </button>
                                <button
                                  type="button"
                                  className="flex-shrink-0 px-3 py-1.5 rounded-lg border border-[#D0D0D0] bg-white text-xs font-medium text-[#6D6D6D] hover:bg-[#F5F5F5]"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFcNoticeFileRows((prev) => prev.map((r) => r.tmNo === row.tmNo ? { ...r, pendingFile: null, fileKey: null, filename: null, fileUrl: null } : r));
                                  }}
                                >
                                  Remove
                                </button>
                              </>
                            )}
                          </div>
                          <input
                            id={`fc-notice-file-input-${row.tmNo}`}
                            type="file"
                            accept=".pdf"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) setFcNoticeFileRows((prev) => prev.map((r) => r.tmNo === row.tmNo ? { ...r, pendingFile: file, filename: file.name } : r));
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}

                <FilePreviewModal
                  open={!!fcNoticePreview}
                  onClose={() => {
                    if (fcNoticePreview?.revokeOnClose) URL.revokeObjectURL(fcNoticePreview.url);
                    setFcNoticePreview(null);
                  }}
                  name={fcNoticePreview?.name ?? ""}
                  url={fcNoticePreview?.url}
                  kind={fcNoticePreview?.kind}
                />

                {fcNoticeUploadSaving && (
                  <p className="text-xs text-[#6D6D6D]" style={{ fontFamily: "Inter, sans-serif" }}>Uploading…</p>
                )}
              </form>
            ) : fcFailSubStep === "notice-to-client" ? (
              <form
                id="formalities-check-fc-fail-notice-form"
                onSubmit={(ev) => void handleFcFailNoticeFormSubmit(ev)}
                className="flex flex-col w-full"
              >
                <NoticeToClientPanel
                  phone={phone}
                  noticeFiles={fcNoticeFileRows
                    .filter((r) => r.fileUrl)
                    .map((r) => ({
                      tmNo: r.tmNo,
                      url: r.fileUrl as string,
                      name: r.filename ?? "Notice PDF",
                      mark: tmEntries.find((t) => t.tmNo === r.tmNo)?.mark,
                    }))}
                  selectedTms={fcNoticeSelectedTms}
                  onToggleTm={(tmNo, checked) =>
                    setFcNoticeSelectedTms((prev) =>
                      checked
                        ? prev.includes(tmNo)
                          ? prev
                          : [...prev, tmNo]
                        : prev.filter((t) => t !== tmNo)
                    )
                  }
                  onToggleAll={(checked) =>
                    setFcNoticeSelectedTms(
                      checked
                        ? fcNoticeFileRows
                            .filter((r) => r.fileUrl)
                            .map((r) => r.tmNo)
                        : []
                    )
                  }
                  onPreview={({ url, name }) =>
                    setFcNoticePreview({ url, name, kind: detectPreviewKind(name) })
                  }
                  hasSentNotice={hasSentFcFailNotice}
                  canSendNotice={canSendFcFailNotice}
                  resending={resendingFcFailNotice}
                  onResend={() => void handleResendFcFailNoticeWhatsApp()}
                />

                <ConfirmMessageModal
                  open={fcFailNoticeConfirm !== null}
                  onClose={() => setFcFailNoticeConfirm(null)}
                  onConfirm={() => void confirmSendFcFailNotice()}
                  disableConfirm={
                    !canSendFcFailNotice ||
                    fcNoticeSelectedTms.length === 0 ||
                    fcNoticePreviewLoading ||
                    (!!fcNoticePreviewData && !fcNoticePreviewData.recipientEmail)
                  }
                  title={
                    fcFailNoticeConfirm === "resend"
                      ? "Confirm Resend Notice to Client"
                      : "Confirm Notice to Client"
                  }
                  previewSlot={
                    <div
                      className="flex flex-col gap-3 p-4"
                      style={{ fontFamily: "Inter, sans-serif" }}
                    >
                      {fcNoticePreviewLoading ? (
                        <p className="text-sm text-[#6d6d6d]">Loading preview…</p>
                      ) : !fcNoticePreviewData ? (
                        <p className="text-sm text-[#6d6d6d]">Preview not available.</p>
                      ) : (
                        <>
                          {fcNoticePreviewData.documents.length > 1 && (
                            <p className="text-xs text-[#6D6D6D]">
                              {fcNoticePreviewData.documents.length} notices will be
                              combined into one PDF and sent together.
                            </p>
                          )}

                          {/* WhatsApp */}
                          <section className="rounded-lg border border-[#EEEEEE] bg-white p-4">
                            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                              <p className="text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                                WhatsApp
                              </p>
                              <span className="text-xs text-[#222]">
                                To:{" "}
                                {fcNoticePreviewData.recipientPhone || "(no phone)"}
                              </span>
                            </div>
                            <WhatsAppMessageBlock
                              message={fcNoticePreviewData.whatsAppMessage}
                            />
                          </section>

                          {/* Email */}
                          <section className="rounded-lg border border-[#EEEEEE] bg-white p-4">
                            <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                              Email
                            </p>
                            {fcNoticePreviewData.recipientEmail ? (
                              <div className="flex flex-col gap-1.5 text-[13px] text-[#222]">
                                <div className="break-words">
                                  <span className="text-[#6D6D6D]">To: </span>
                                  <span className="break-all">
                                    {fcNoticePreviewData.recipientEmail}
                                  </span>
                                </div>
                                <div className="break-words">
                                  <span className="text-[#6D6D6D]">Subject: </span>
                                  {fcNoticePreviewData.emailSubject}
                                </div>
                                <div className="mt-2 whitespace-pre-wrap break-words rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] p-3">
                                  {fcNoticePreviewData.emailMessage}
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-[#D92D20]">
                                No email on file for this client. Add a client email
                                before sending — this notice is sent by email and
                                WhatsApp.
                              </p>
                            )}
                          </section>

                          {/* Attachments */}
                          <section className="rounded-lg border border-[#EEEEEE] bg-white p-4">
                            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                              {fcNoticePreviewData.documents.length > 1
                                ? "Attachments"
                                : "Attachment"}
                            </p>
                            <div className="flex flex-col gap-2">
                              {fcNoticePreviewData.documents.map((d) => (
                                <div
                                  key={d.tmNo}
                                  className="flex items-center gap-3 rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7] px-3 py-2"
                                >
                                  <img
                                    src={pdfIcon}
                                    alt=""
                                    className="h-5 w-5 shrink-0"
                                  />
                                  <div className="flex flex-1 min-w-0 flex-col">
                                    <span className="truncate text-xs font-medium text-[#222]">
                                      {d.filename ?? "Notice PDF"}
                                    </span>
                                    <span className="truncate text-[11px] text-[#6D6D6D]">
                                      TM No. {d.tmNo}
                                      {d.mark ? ` · ${d.mark}` : ""}
                                    </span>
                                  </div>
                                  {d.fileUrl && (
                                    <button
                                      type="button"
                                      className="flex-shrink-0 text-xs font-medium text-[#2E3037] underline underline-offset-2"
                                      onClick={() =>
                                        setFcNoticePreview({
                                          url: d.fileUrl as string,
                                          name: d.filename ?? "Notice PDF",
                                          kind: detectPreviewKind(d.filename ?? ""),
                                        })
                                      }
                                    >
                                      Preview
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </section>
                        </>
                      )}
                    </div>
                  }
                />

                <FilePreviewModal
                  open={!!fcNoticePreview}
                  onClose={() => {
                    if (fcNoticePreview?.revokeOnClose) URL.revokeObjectURL(fcNoticePreview.url);
                    setFcNoticePreview(null);
                  }}
                  name={fcNoticePreview?.name ?? ""}
                  url={fcNoticePreview?.url}
                  kind={fcNoticePreview?.kind}
                />
              </form>
            ) : fcFailSubStep === "inform-client" ? (
              <form
                id="formalities-check-fc-fail-inform-client-form"
                onSubmit={(ev) => void handleFcFailInformFormSubmit(ev)}
                className="flex flex-col w-full"
              >
                <InformClientPanel
                  leadId={leadId}
                  phone={phone}
                  hasSentInform={hasSentFcFailInform}
                  resending={resendingFcFailInform}
                  onResend={() => void handleResendFcFailInformWhatsApp()}
                />
              </form>
            ) : fcFailSubStep === "invoice-for-fc-failure" ? (
              <form
                id="formalities-check-fc-fail-invoice-form"
                onSubmit={(ev) => void handleFcFailInvoiceFormSubmit(ev)}
                className="flex flex-col w-full"
              >
                <FcFailInvoicePanel
                  ref={fcFailInvoicePanelRef}
                  leadId={leadId}
                  phone={phone}
                  hasSentInvoice={hasSentFcFailInvoice}
                  resending={resendingFcFailInvoice}
                  onResend={() => void handleResendFcFailInvoiceWhatsApp()}
                  onInvoiceSendReadyChange={setFcFailInvoiceSendReady}
                  selectedTmNo={selectedEntry.tmNo}
                />
              </form>
            ) : fcFailSubStep === "call-client" ? (
              <form
                id="formalities-check-fc-fail-call-client-form"
                onSubmit={handleFcFailCallClientFormSubmit}
                className="flex flex-col w-full"
              >
                <CallClientPanel
                  leadId={leadId}
                  phone={phone}
                  recorded={hasRecordedFcFailCallClient}
                  onRecorded={() => setHasRecordedFcFailCallClient(true)}
                />
              </form>
            ) : fcFailSubStep === "client-confirmation" ? (
              <form
                id="formalities-check-fc-fail-client-confirmation-form"
                onSubmit={handleFcFailConfirmationFormSubmit}
                className="flex flex-col w-full"
              >
                <ClientConfirmationPanel
                  ref={clientConfirmationPanelRef}
                  leadId={leadId}
                  tmNos={displayTmEntries.map((e) => e.tmNo)}
                  selectedTmNo={selectedEntry.tmNo}
                />
              </form>
            ) : fcFailSubStep === "documents" ? (
              <form
                id="formalities-check-fc-fail-documents-form"
                onSubmit={(ev) => void handleFcFailDocumentsFormSubmit(ev)}
                className="flex flex-col w-full"
              >
                <DocumentsPanel />
              </form>
            ) : fcFailSubStep === "filing-of-reply" ? (
              <form
                id="formalities-check-fc-fail-filing-of-reply-form"
                onSubmit={(ev) => void handleFcFailFilingOfReplyFormSubmit(ev)}
                className="flex flex-col w-full"
              >
                <FilingOfReplyPanel ref={filingOfReplyPanelRef} leadId={leadId} />
              </form>
            ) : fcFailSubStep === "inform-client-done" ? (
              <form
                id="formalities-check-fc-fail-inform-done-form"
                onSubmit={(ev) => void handleFcFailInformDoneFormSubmit(ev)}
                className="flex flex-col w-full"
              >
                <InformClientDonePanel
                  leadId={leadId}
                  phone={phone}
                  hasSentDone={hasSentFcFailInformDone}
                  resending={resendingFcFailInformDone}
                  onResend={() => void handleResendFcFailInformDoneWhatsApp()}
                  onInformDoneSendReadyChange={setFcFailInformDoneSendReady}
                />
              </form>
            ) : (
              <div className="flex flex-col gap-3">
                <p
                  className="text-sm text-[#6D6D6D]"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  {
                    FC_FAIL_SUB_STEPS.find((s) => s.id === fcFailSubStep)
                      ?.label
                  }{" "}
                  — Coming soon
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
